/* DW QUICK ADD - ASRV-style colour + size panel on product cards.
   Built 2026-09-19 for the sale theme (CRO Sandbox - The Archive closes). Revision 2, 2026-09-19,
   after the three-lens critique (behaviour / code-risk / edge-data).

   REVISION 3, 2026-09-20 - Pedram's asks of that morning plus two CEO-pass fixes, deployed
   byte-identical to themes 165146788069 and 164976984293.
   1 SPEED: the 60ms open delay is gone (HOVER_IN), panels are BUILT at idle for cards in and near
     the viewport (warmArm / warmObserve / prebuild), swatch thumbnails are asked for at the size
     they are drawn (SWATCH_PX) and warmed ahead (thumbPump). Measured: pointerenter to size row
     painted went from 191-237ms cold / 95-163ms warm to about 30ms, with no skeleton drawn.
   2 CARD IMAGE SCROLLER: arrows, swipe, position bar - see the block above useColor().
   3 SIZE CHIPS: one fixed width and gap whatever the count - .dw-qa__sizes in the stylesheet.
   Plus the wishlist heart off the cards (CSS) and the clearance disclosure as one quiet line with
   product-level wording where the whole product is final sale (statusText / isFinalProduct).

   REVISION 4, 2026-09-21 - the first critique round on revision 3 (speed / scroller-chips /
   regression lenses). Deployed byte-identical to both themes again.
   - A phone shopper who only scrolls fetches nothing: warming arms on a recognised swipe or a "+"
     tap, never on touchstart (speed B1). The bar is drawn at rest from a photo count the Liquid
     prints (data-photos), so the swipe affordance is there before anything is fetched.
   - The swipe follows the finger, with the next photo sliding in beside it (scroller S2).
   - The swipe stands down inside sideways product rows, so Focal's home carousels scroll again
     (regression B1).
   - Six sizes sit on one row on a phone, and every clothing-size row has one chip width: the phone
     row is six equal tracks, and XXL / XXXL are drawn 2XL / 3XL (speed B2, scroller B1).
   - CPU budget for warming: one panel built per idle callback, nothing while the page scrolls,
     nothing for cards that have scrolled away (speed S2).
   - A warm card re-reads stock in the background when its data is older than the cache allows,
     and repaints the chips in place if anything sold (regression S2).
   - Both photo arrows show and wrap; the photo an arrow will show is fetched when the pointer
     settles on it (scroller S3, speed S3); photo renditions round up (scroller S5); the card's own
     photo is photo one (regression S7); swatch thumbs are warmed around the selected colour
     (speed S4); the clearance line uses the product page's words exactly (regression S1).

   HOW IT WORKS
   - snippets/dw-quick-add.liquid puts a <dw-quick-add> element on every card that already shows
     Focal's quick-buy disc. It carries only the handle, the card's variant id and the card's colour
     name - no variant JSON is printed into the grid.
   - The first time a panel opens, the product is fetched from /products/<handle>.js and cached in a
     module-level Map keyed by handle, so the twelve colour-split cards of one product share ONE
     request. A mouse hover over the disc warms that cache early.
   - Add to cart goes through the theme's own path: the snippet renders a real Focal
     <form is="product-form">, this script writes the chosen variant into its hidden id input and
     calls requestSubmit(). Focal's ProductForm posts to /cart/add.js, then fires variant:added and
     cart:refresh{openMiniCart:true} on success, which is what makes the Rebuy Smart Cart drawer
     open. Success or failure is read from Focal's own cart-notification:show event
     (detail.status === 'success'), because Focal fires a bare cart:refresh on failure too.
     Nothing about the cart is re-implemented here.

   PRICE (critique blocker QA-1 / B1). Changing colour used to repaint only the selling price and
   leave the struck-through compare-at price and the discount chip behind, so a card could show a
   sale that does not exist, or hide one that does. The whole .price-list is now rebuilt from the
   chosen colour's own cheapest variant, in the same shape snippets/product-item.liquid renders
   server-side: sale + compare pair when that colour is discounted, a plain price when it is not,
   and the locale's own "From ..." wording when that colour's sizes are not all one price. The card
   also snapshots its server-rendered state on first open and is restored byte-for-byte when the
   shopper comes back to the colour the card was rendered for.

   WORDING. The two labels the rebuilt price line needs - the screen-reader-only "Sale price" and
   "Regular price" - are lifted off the cards the server already rendered on this page, so they
   follow the storefront language with no extra Liquid and no extra page weight. The English
   constants below are only a fallback for a page that has no discounted card to learn from.
   layout/theme.liquid is deliberately NOT touched by this feature beyond the two asset tags that
   were already there.

   REVERT: turn off the "ASRV-style quick add" theme setting, or delete this asset, its <link> and
   <script> in layout/theme.liquid, assets/dw-quick-add.css, snippets/dw-quick-add.liquid and the
   two dw_qa blocks in snippets/product-item.liquid. */

(function () {
  'use strict';

  if (!window.customElements || !window.fetch || !('closest' in Element.prototype)) return;

  var COLOR_RE = /colou?r|shade/i;
  var SIZE_RE = /size/i;
  var CACHE = new Map();
  var openEl = null;

  var PRICE_TOKEN = '@@';

  var FALLBACK = {
    fromPrice: 'From ' + PRICE_TOKEN,
    salePrice: 'Sale price',
    regularPrice: 'Regular price',
    soldOut: 'Sold out',
    previousColors: 'Previous colours',
    moreColors: 'More colours',
    colorLabel: 'Colour',
    sizeLabel: 'Size',
    addLabel: 'Add',
    addGroupLabel: 'Add to bag',
    optionsError: 'Options could not be loaded.',
    openProduct: 'Open the product page',
    addError: 'Could not add that size.',
    /* CEO PASS, 2026-09-20: one string used to carry two different facts, and on an Archive card -
       where the WHOLE product is final sale - "Clearance color" read as though one colour had been
       singled out. Two strings, chosen by isFinalProduct().
       REVISION 4 (critique round 1, regression S1): the product page's own words, letter for letter
       (snippets/final-sale-notice.liquid). Revision 3 had paraphrased both to make them fit one
       line at 360, and the Archive paraphrase dropped "or exchanges" - the card promised less than
       the terms. They measure 170px and 205px at 10px: one line on a desktop panel (308px), two
       RESERVED lines on a phone (.dw-qa__status--final), so the panel still never changes height. */
    finalSale: 'Clearance color - final sale, non-refundable.',
    finalSaleProduct: 'Final sale - no returns or exchanges.',
    soldOutLabel: 'Sold out',
    prevPhoto: 'Previous photo',
    nextPhoto: 'Next photo'
  };

  function str(key) {
    return FALLBACK[key];
  }

  /* Learned once per page from the cards the server rendered. Focal puts a visually-hidden
     "Sale price" inside every .price span and a "Regular price" inside every .price--compare span,
     in the storefront's own language, and writes the locale's "From <price>" wording into a card
     whose sizes are not all one price. Reading them back is how the repainted price line stays in
     the shopper's language without printing a single extra byte into the grid. */
  var LEARNED = { sale: null, regular: null, from: null };

  function learn(priceList) {
    if (!priceList) return;
    var main = priceList.querySelector('.price:not(.price--compare)');
    if (main) {
      var saleLabel = main.querySelector('.visually-hidden');
      if (saleLabel && !LEARNED.sale) LEARNED.sale = saleLabel.textContent;
      /* "From $16.99" -> "From @@". Only a card whose price varies carries this wording, and only
         its money <span> is swapped out, so the sentence around it survives translation. */
      if (!LEARNED.from) {
        var moneyEl = main.querySelector('.money');
        var text = main.textContent || '';
        if (saleLabel) text = text.replace(saleLabel.textContent, '');
        var amount = moneyEl && moneyEl.textContent;
        if (amount && text.replace(/\s+/g, ' ').trim() !== amount.trim()) {
          LEARNED.from = text.replace(/\s+/g, ' ').trim().split(amount.trim()).join(PRICE_TOKEN);
        }
      }
    }
    var compare = priceList.querySelector('.price--compare .visually-hidden');
    if (compare && !LEARNED.regular) LEARNED.regular = compare.textContent;
  }

  function learned(key, fallbackKey) {
    return LEARNED[key] || str(fallbackKey);
  }

  /* Last line of defence on form-id uniqueness (critique S4). The snippet's id is product + variant,
     which is unique for every card of a colour-split product; the only way two can collide is the
     same variant card rendered twice in two rows of one page. Set through setAttribute on purpose:
     form.id is NOT the id attribute on a <form>, it is the control named "id" (Focal's ProductForm
     relies on exactly that), so assigning to it would be a bug. */
  var SEEN_IDS = Object.create(null);
  var idSeq = 0;
  function uniqueFormId(form) {
    var id = form.getAttribute('id');
    if (!id) return;
    if (SEEN_IDS[id]) {
      id = id + '-' + (++idSeq);
      form.setAttribute('id', id);
    }
    SEEN_IDS[id] = true;
  }

  /* One sweep of the grid the first time anybody opens a panel - not at page load, so it costs
     nothing until the feature is used. One card on the page is usually enough to supply all three
     wordings, but during the sale the discounted card and the "From ..." card are rarely the same
     one, so the whole grid is asked. */
  var learnedPage = false;
  function learnPage() {
    if (learnedPage) return;
    learnedPage = true;
    var lists = document.querySelectorAll('.product-item-meta .price-list');
    for (var i = 0; i < lists.length; i++) {
      learn(lists[i]);
      if (LEARNED.sale && LEARNED.regular && LEARNED.from) return;
    }
  }

  /* CRITIQUE S1 (edge-data). paintCard() can move a card onto a colour with nothing left to sell,
     and the card kept the label list the server rendered - which carries "Sold out" only when the
     WHOLE product was unavailable at render time. So a sold-out colour repainted into a card that
     looked buyable. The wording and the exact markup are lifted off a card the server already
     rendered sold out, the same way the price wordings are, so a translated storefront gets its own
     words; the English below is only the fallback for a grid with nothing sold out on it. */
  var soldOutHTML = null;
  var soldOutLearned = false;
  function soldOutLabelHTML() {
    if (!soldOutLearned) {
      soldOutLearned = true;
      var node = document.querySelector('.product-item__label-list .label--subdued');
      if (node) soldOutHTML = node.outerHTML;
    }
    return soldOutHTML || '<span class="label label--subdued">' + esc(str('soldOutLabel')) + '</span>';
  }

  function root() {
    return (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
  }

  function settings() {
    return (window.themeVariables && window.themeVariables.settings) || {};
  }

  /* Format cents with the shop's own money format, so a colour priced differently from the one the
     card was rendered with updates the card's price line in the same shape as the server's. */
  function money(cents) {
    var s = settings();
    var format = (s.currencyCodeEnabled ? s.moneyWithCurrencyFormat : s.moneyFormat) || s.moneyFormat;
    if (!format) return '';
    var value = Number(cents) / 100;
    return format.replace(/\{\{\s*(\w+)\s*\}\}/g, function (match, name) {
      var decimals = name.indexOf('no_decimals') > -1 ? 0 : 2;
      var comma = name.indexOf('comma_separator') > -1;
      var parts = value.toFixed(decimals).split('.');
      var whole = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, comma ? '.' : ',');
      return parts[1] ? whole + (comma ? ',' : '.') + parts[1] : whole;
    });
  }

  function sized(url, width) {
    if (!url) return '';
    return url + (url.indexOf('?') > -1 ? '&' : '?') + 'width=' + width;
  }

  /* The file name of a Shopify CDN url, which is what identifies one photo across the different
     ?width= renditions the grid, the panel and the scroller each ask for. Three copies of this
     expression were already inline in currentColor() and snapshot(); they all call this now. */
  function fileOf(url) {
    return String(url == null ? '' : url).split('?')[0].split('/').pop();
  }

  /* SPEED, Pedram 2026-09-20: "request swatch thumbnails at the size they are drawn".
     The swatch is 44px on a desktop card and 40px on a phone, so the old fixed ?width=110 was
     asking for 2.5x on desktop and 2.75x on a phone. One number for the whole page keeps every
     card asking the CDN for the same rendition, so the second card's swatches are cache hits.
     Capped at 2x because a 3x phone gains nothing visible on a 40px square and pays for it. */
  var SWATCH_PX = (function () {
    var dpr = window.devicePixelRatio || 1;
    return Math.round(44 * (dpr > 2 ? 2 : dpr));
  })();

  function esc(value) {
    return String(value == null ? '' : value).replace(/[&<>"']/g, function (ch) {
      return { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[ch];
    });
  }

  /* CRITIQUE QA4-S1. The variant list used to be cached for the whole life of the page with no
     expiry, so a size that sold out while the shopper was still scrolling the grid stayed un-faded
     and tappable. Shopify refuses the add, so nothing wrong is ever sold, but the shopper got the
     panel's "could not be added" instead of an honest faded chip. That matters this week in
     particular: the Archive stock is thin enough that several colours are down to their last size,
     which is exactly the chip most likely to go. Two cheap guards - a short time to live, and
     dropVariants() below, which a refused add calls so the next open re-reads real stock. */
  var CACHE_TTL = 90000;

  function dropVariants(handle) {
    if (handle) CACHE['delete'](handle);
  }

  function fetchProduct(handle) {
    var entry = CACHE.get(handle);
    if (entry && (Date.now() - entry.at) <= CACHE_TTL) return entry.promise;
    var promise = fetch(root() + 'products/' + handle + '.js', { headers: { Accept: 'application/json' } })
      .then(function (res) {
        if (!res.ok) throw new Error('HTTP ' + res.status);
        return res.json();
      })
      .catch(function (err) {
        CACHE['delete'](handle);
        throw err;
      });
    var at = Date.now();
    /* Which fetch a card's model came from, so open() can tell a model older than the cache
       (refreshIfStale, regression S2). */
    promise.at = at;
    CACHE.set(handle, { at: at, promise: promise });
    return promise;
  }

  /* One model per fetched product, shared by every colour card of it: build() is the same work for
     each of the twelve cards of a colour-split product, so it is done once. Nothing mutates a model
     after build() returns (photosOf() copies before it reorders), which is what makes sharing safe. */
  var MODELS = typeof WeakMap === 'function' ? new WeakMap() : null;
  function modelOf(product) {
    var model = MODELS ? MODELS.get(product) : null;
    if (!model) {
      model = build(product);
      if (MODELS) MODELS.set(product, model);
    }
    return model;
  }

  /* What a stock refresh compares: which sizes exist, which can be sold, and at what price. */
  function stockKey(model) {
    if (model.key) return model.key;
    var key = '';
    model.colors.forEach(function (color) {
      key += '|' + color.value + ':';
      color.sizes.forEach(function (size) {
        key += size.id + (size.available ? '+' : '-') + size.price + ',';
      });
    });
    model.key = key;
    return key;
  }

  /* ASK 3, critique round 1 (scroller B1 / speed B2). "XXXL" is four characters and cannot sit in a
     size-shaped chip on a phone, which is why revision 3 gave arc-define-t-shirt wider chips than
     the coreflex stringer beside it - exactly the unequal row Pedram objected to. The catalogue
     already writes the same sizes both ways (coreflex says 2XL / 3XL, arc-define says XXL / XXXL),
     so the chip draws the short form everywhere: XXL -> 2XL, XXXL -> 3XL. Only the drawn text
     changes; the variant id, the option value and everything submitted are untouched. */
  function chipText(label) {
    var match = /^(X{2,})([SL])$/i.exec(String(label == null ? '' : label).trim());
    return match ? match[1].length + 'X' + match[2].toUpperCase() : label;
  }

  function optionNames(product) {
    return (product.options || []).map(function (option) {
      return (typeof option === 'string' ? option : option && option.name) || '';
    });
  }

  /* An option's own name is the best label for its row - "Length", "Denominations" - but DW's
     catalogue carries admin-side names like "Knee-wrap-color", "Necklace-size" and "Apparel-color",
     and a screen reader then reads the database field out loud. Keep a name a person would say;
     swap anything shaped like an internal key for the plain word (critique R2-S2). */
  function groupLabel(name, fallbackKey) {
    var clean = String(name == null ? '' : name).trim();
    if (!clean || /[-_]/.test(clean)) return str(fallbackKey);
    return clean;
  }

  function optionIndex(names, pattern) {
    for (var i = 0; i < names.length; i++) {
      if (pattern.test(names[i])) return i;
    }
    return -1;
  }

  function normColor(value) {
    return String(value == null ? '' : value).toLowerCase().replace(/\s+/g, ' ').trim();
  }

  /* WHICH PHOTOS BELONG TO WHICH COLOUR - what the card image scroller (ask 2) needs, and what the
     note above paintCard() says this file cannot know. That note is right about the two fields it
     was looking at: variants[].featured_image gives one photo, and guessing a colour's RUN of
     photos from positions in product.images is exactly the mistake that shows a shopper a colour
     they did not pick.
     product.media[].alt is not a guess. DW's catalogue tags every image with the merchant's own
     "#color_<option value>" convention. Measured across the 24 products on page 1 of
     /collections/all: 22 tag every image and every colour of them finds its photos; only
     v-waist-honeycomb-scrunch-bum-short and flo-cropped-t-shirt tag nothing. Case is inconsistent
     even inside one product ("#color_Dark Grey" then "#Color_titanium"), so both sides are
     normalised. Returns null when a product tags nothing; build() then falls back to the one photo
     it can prove - that colour's own featured image - never a neighbour's, and one photo means no
     scroller at all. */
  function photosByColor(product) {
    var media = product.media || [];
    var out = Object.create(null);
    var found = false;
    for (var i = 0; i < media.length; i++) {
      var item = media[i];
      if (!item || (item.media_type && item.media_type !== 'image')) continue;
      var src = item.src || (item.preview_image && item.preview_image.src);
      if (!src) continue;
      var match = /^#colou?r_(.+)$/i.exec(String(item.alt == null ? '' : item.alt).trim());
      if (!match) continue;
      var key = normColor(match[1]);
      if (!out[key]) out[key] = [];
      out[key].push(src);
      found = true;
    }
    return found ? out : null;
  }

  /* The photo list for one colour, first photo first. Never longer than this: a position bar with
     more segments than that stops being readable at 64px on a phone, and no colour on the store has
     more than ten photos today (the most is Charcoal on power-play-oversized-shirt). */
  var PHOTO_MAX = 12;

  function photosFor(group, tagged, product, onlyColor) {
    var list = tagged ? tagged[normColor(group.value)] : null;
    /* A listing with no colour option at all - a wrist wrap, a gift card - has one group, and every
       photo on the product belongs to it. */
    if ((!list || !list.length) && onlyColor) list = (product.images || []).slice();
    if (!list || !list.length) return group.image ? [group.image] : [];
    list = list.slice(0, PHOTO_MAX);
    /* The card is already showing this colour's featured image, so that is photo one and the
       scroller starts where the shopper's eye already is. */
    if (group.image) {
      var lead = fileOf(group.image);
      for (var i = 0; i < list.length; i++) {
        if (fileOf(list[i]) === lead) { list.splice(i, 1); break; }
      }
      list.unshift(group.image);
    }
    return list;
  }

  /* Reduce a product to what the panel draws: an ordered list of colours, each with a thumbnail and
     its own size row. Copes with all three DW listing shapes - colour + size, colour only (wrist
     wraps, knee wraps), and listings with no colour option at all.
     compare_at_price is carried alongside price because the card's whole price line, including the
     struck-through "was" price, is rebuilt from it when the colour changes. */
  function build(product) {
    var names = optionNames(product);
    var colorIndex = optionIndex(names, COLOR_RE);
    var sizeIndex = optionIndex(names, SIZE_RE);
    if (sizeIndex === colorIndex) sizeIndex = -1;
    if (sizeIndex < 0) {
      for (var n = 0; n < names.length; n++) {
        if (n !== colorIndex) { sizeIndex = n; break; }
      }
    }

    var colors = [];
    var byValue = Object.create(null);

    (product.variants || []).forEach(function (variant) {
      if (!variant || !variant.id) return;
      var key = colorIndex > -1 ? String(variant['option' + (colorIndex + 1)] || '') : '';
      var group = byValue[key];
      if (!group) {
        group = byValue[key] = { value: key, image: null, sizes: [], seen: Object.create(null), available: false };
        colors.push(group);
      }
      if (!group.image && variant.featured_image && variant.featured_image.src) {
        group.image = variant.featured_image.src;
      }
      if (variant.available) group.available = true;

      var label = sizeIndex > -1 ? String(variant['option' + (sizeIndex + 1)] || '') : '';
      var existing = group.seen[label];
      if (existing) {
        /* Same size listed twice (a third option, or a duplicate). Keep whichever one can be sold. */
        if (!existing.available && variant.available) {
          existing.id = variant.id;
          existing.available = true;
          existing.price = variant.price;
          existing.compare = variant.compare_at_price;
        }
        return;
      }
      var entry = {
        id: variant.id,
        label: label,
        available: !!variant.available,
        price: variant.price,
        compare: variant.compare_at_price
      };
      group.seen[label] = entry;
      group.sizes.push(entry);
    });

    var tagged = photosByColor(product);
    var onlyColor = colors.length === 1;
    colors.forEach(function (group) {
      if (!group.image) group.image = product.featured_image || '';
      delete group.seen;
      /* Ask 2: the photos this colour's scroller moves through. Costs no extra request - it is read
         out of the same /products/<handle>.js the panel was already fetching. */
      group.photos = photosFor(group, tagged, product, onlyColor);
    });

    return {
      title: product.title || '',
      colors: colors,
      hasSizes: sizeIndex > -1,
      /* The second row is not always sizes - a gift card's is denominations - so the group is
         labelled with the option's own name for screen readers. */
      sizeLabel: sizeIndex > -1 ? groupLabel(names[sizeIndex], 'sizeLabel') : str('addGroupLabel'),
      /* A clothing-size row takes the one fixed chip width (ask 3). Any other second option - a gift
         card's denominations - keeps a width that fits its labels. */
      sizeish: sizeIndex > -1 && SIZE_RE.test(names[sizeIndex]),
      colorLabel: colorIndex > -1 ? groupLabel(names[colorIndex], 'colorLabel') : str('colorLabel'),
      url: product.url || (root() + 'products/' + product.handle)
    };
  }

  /* The price facts of one colour, in the same terms snippets/product-item.liquid works in:
     Focal prices a card off its CHEAPEST variant (product.variants | sort: 'price' | first),
     sold out or not, and shows the locale's "From ..." wording when the sizes are not all one
     price. Mirroring that exactly is what keeps a repainted card identical to the card the grid
     would have rendered for that colour. */
  function priceFacts(color) {
    var min = null;
    var max = null;
    var cheapest = null;
    (color.sizes || []).forEach(function (entry) {
      if (entry.price == null) return;
      if (min === null || entry.price < min) { min = entry.price; cheapest = entry; }
      if (max === null || entry.price > max) max = entry.price;
    });
    if (min === null) return null;
    var compare = cheapest && cheapest.compare != null ? Number(cheapest.compare) : 0;
    return { min: min, varies: max > min, compare: compare, onSale: compare > min };
  }

  function chevron(direction) {
    var path = direction === 'prev' ? 'M7 1L2 6l5 5' : 'M2 1l5 5-5 5';
    return '<svg viewBox="0 0 9 12" fill="none" aria-hidden="true" focusable="false">' +
      '<path d="' + path + '" stroke="currentColor" stroke-width="1.6" stroke-linecap="square"/></svg>';
  }


  /* ==============================================================================================
     HOVER ON POINTER DEVICES - Pedram's ask 2, 2026-09-20.
     "On ASRV desktop, when you hover the mouse on a product the colours and sizes automatically go
     up, and when you go to another product it drops down and the other one goes up."

     One timer for the whole grid, not one per card, because only one panel is ever open.

     The three things a naive mouseenter handler gets wrong, and what is done about each:

     - It fights taps. The gate here is the media query (hover: hover) and (pointer: fine), READ AT
       EVENT TIME rather than cached, plus a pointerType check on the event itself. A phone, a
       tablet and a 2-in-1 that has just been folded into tablet mode all fail it. No user-agent
       sniffing anywhere in this file.
     - It flickers when the pointer crosses the panel. The listeners are bound to the CARD, and
       pointerenter / pointerleave do not fire while the pointer moves between a card's own
       descendants - so travelling from the photo onto the swatch strip is not a leave, and the
       panel does not blink.
     - It opens while the shopper is scrolling with the pointer resting over the grid. Scrolling
       drags cards UNDER a still pointer, and each one fires pointerenter with no intent behind it.
       So an open needs two things to be true at the moment the timer fires: the page has been
       still for SCROLL_QUIET ms, and the POINTER has moved more recently than the page did. Both
       are false mid-wheel. A pointermove re-arms the timer, which is what makes the panel come up
       the instant the shopper actually moves after a scroll rather than a beat later.
     ============================================================================================== */
  /* SPEED, 2026-09-20, "the spec's open on hover with no artificial delay". This was 60ms of
     deliberate waiting and it was the biggest single part of what Pedram felt: measured at 1440
     before the change, a card whose data was already in hand painted its size row 95-163ms after
     pointerenter, 60ms of it this timer doing nothing. Zero, not deleted - the timer still runs one
     tick later, because the three guards inside it are what stop a panel opening under a pointer
     that is only sitting still while the page scrolls past. HOVER_OUT is untouched. */
  var HOVER_IN = 0;
  var HOVER_OUT = 120;    /* card-v2-spec: closes on mouseleave after about 120ms */
  var SCROLL_QUIET = 250;
  /* Fallback for the bug fix below, used only by a browser that gave us no pointerdown to judge.
     Chrome, Safari and Firefox all fire one, so in practice this never decides anything. */
  var REBUILD_GUARD = 350;

  var hoverEl = null;
  var hoverTimer = null;
  var lastScroll = 0;
  var lastMove = 0;

  function now() { return Date.now(); }

  function hoverCapable() {
    return !!(window.matchMedia && window.matchMedia('(hover: hover) and (pointer: fine)').matches);
  }

  /* A pointer event with no pointerType at all is a mouse on an older engine; touch and pen say so. */
  function isMouse(event) {
    return !event.pointerType || event.pointerType === 'mouse';
  }

  function armOpen() {
    if (hoverTimer) return;
    hoverTimer = setTimeout(function () {
      hoverTimer = null;
      var el = hoverEl;
      if (!el || el.isOpen || el._busy) return;
      if (el._hoverHold && now() < el._hoverHold) return;
      if (now() - lastScroll < SCROLL_QUIET) return;
      if (lastMove < lastScroll) return;
      if (!hoverCapable()) return;
      el.open(true);
    }, HOVER_IN);
  }

  function hoverBind() {
    if (hoverBind.done) return;
    hoverBind.done = true;
    /* capture: true so a scroll inside any scroller on the page counts, not only the document's. */
    document.addEventListener('scroll', function () { lastScroll = now(); }, { passive: true, capture: true });
    document.addEventListener('wheel', function () { lastScroll = now(); }, { passive: true, capture: true });
    document.addEventListener('pointermove', function (event) {
      if (!isMouse(event)) return;
      lastMove = now();
      /* The shopper is here, with a pointer, and moving it. That is the moment warming is allowed
         to start - see warmArm(). */
      warmArm();
      if (hoverEl && !hoverEl.isOpen) armOpen();
    }, { passive: true });
  }

  /* WARM AHEAD - the other half of the speed ask. Hovering a card used to be the first time
     anything was fetched, so every first hover paid for a round trip before a swatch could be drawn.

     THE ONE JUDGEMENT CALL HERE, worth reading before changing it. The spec asks for two things
     that pull against each other: warm the viewport "after the page is idle", and "nothing fetched
     for shoppers who never hover or tap". Warming at load breaks the second. So warming is ARMED,
     not automatic: nothing leaves the browser until the shopper shows intent with a pointer.
     - Desktop: the first mouse move anywhere (hoverBind). That is within a second of arrival and
       long before the pointer lands on any card.
     - Phone, REVISION 4 (critique round 1, speed B1 / scroller S4 / regression S5): a finger landing
       on a card is NOT intent. You cannot scroll a phone grid without your finger landing on cards,
       and revision 3 armed on touchstart - measured by the critique at 390 on /collections/best-
       sellers: 8 plain upward flicks and no tap fetched 15 product JSONs and 49 thumbnails, 219KB.
       Warming now arms only on a gesture that is recognised as a SWIPE on the photo (the first
       touchmove that is more sideways than up-and-down, past 10px - onCardMove) or on a tap of the
       "+" (open()). A shopper who only scrolls fetches nothing at all.
     One request per HANDLE however many colour cards it has - fetchProduct() is keyed on the handle
     and hands back the same promise. Drained WARM_MAX at a time so warming never competes with the
     images being looked at.

     CPU BUDGET, REVISION 4 (critique round 1, speed S2). Measured by the critique at 4x CPU on a
     brisk desktop scroll: nine 58-114ms frames with this feature's script in them, because
     warmPump built every queued colour card of an already-cached handle in one loop, straight out of
     the fetch's .then, and never looked at where the cards had got to. Now:
     - fetching and BUILDING are two queues. A product that arrives is only queued for building;
       buildPump() builds ONE card per idle callback, and only when the frame has 8ms or more left;
     - every pump holds while the page has scrolled in the last WARM_HOLD ms (later());
     - a card that has scrolled back out of its ring by the time its turn comes is skipped, and the
       observers queue it again if it comes back (_near / _thumbNear, kept current by the observers
       instead of unobserving on first sight). */
  var WARM_MAX = 3;
  var WARM_HOLD = 150;
  var warmQueue = [];
  var warmActive = 0;
  var warmArmed = false;
  var buildQueue = [];
  var warmObserver = null;
  var thumbObserver = null;

  function idle(fn) {
    if (window.requestIdleCallback) window.requestIdleCallback(fn, { timeout: 1000 });
    else setTimeout(function () { fn(null); }, 60);
  }

  /* One pending run per pump, however many callers ask for one, and none while the page is moving:
     the shopper is watching the scroll, and a frame spent building a panel there is a dropped frame. */
  function later(pump) {
    if (pump.queued) return;
    pump.queued = true;
    var go = function (deadline) {
      var wait = WARM_HOLD - (now() - lastScroll);
      if (wait > 0) {
        setTimeout(function () { idle(go); }, wait);
        return;
      }
      pump.queued = false;
      pump(deadline);
    };
    idle(go);
  }

  function warmArm() {
    if (warmArmed) return;
    warmArmed = true;
    later(warmPump);
  }

  function warmPump() {
    if (!warmArmed) return;
    while (warmActive < WARM_MAX && warmQueue.length) {
      var el = warmQueue.shift();
      el._queued = false;
      if (!el._ready || el._warmed || !el.isConnected) continue;
      /* Left the ring while it waited. The observer puts it back if it returns. */
      if (el._near === false) continue;
      el._warmed = true;
      /* A handle already in the cache costs nothing and must not hold a concurrency slot, or one
         product with twelve cards would stall the queue behind itself twelve times. */
      var entry = CACHE.get(el.handle);
      var counted = !entry || (now() - entry.at) > CACHE_TTL;
      if (counted) warmActive++;
      warmOne(el, counted);
    }
    /* Product JSON first, always: thumbnails only start once it has drained. */
    if (!warmActive && !warmQueue.length && thumbQueue.length) later(thumbPump);
  }

  function warmOne(el, counted) {
    var release = function () {
      if (!counted) return;
      warmActive--;
      later(warmPump);
    };
    var request = fetchProduct(el.handle);
    request.then(function (product) {
      release();
      el._product = product;
      el._productAt = request.at;
      buildQueue.push(el);
      later(buildPump);
    }, function () {
      release();
      el._warmed = false;
    });
  }

  /* The panel's DOM, one card per idle callback and only while that callback has room. A card that
     has scrolled out of its ring is PARKED with its product in hand, and built if it comes back. */
  function buildPump(deadline) {
    while (buildQueue.length) {
      if (deadline && !deadline.didTimeout && deadline.timeRemaining() < 8) break;
      var el = buildQueue.shift();
      var product = el._product;
      if (!product || el._built || el.isOpen || !el.isConnected) {
        el._product = null;
        continue;
      }
      if (el._near === false) {
        el._parked = true;
        continue;
      }
      el._product = null;
      try { el.prebuild(product, el._productAt); } catch (e) {}
      break;
    }
    if (buildQueue.length) later(buildPump);
  }

  /* Cards enter the queue as they come into view, and keep entering as the shopper scrolls, which
     is the whole of "keep warming on scroll". 700px of root margin is about one more row above and
     below at every width the store is used at, so a card is usually warm a beat before it is even
     on screen. Silently does nothing on a browser with no IntersectionObserver: those fall back to
     the hover warm that has always been there.
     Both observers now keep watching (revision 4): whether a card is still near is what the pumps
     ask before spending anything on it. The JSON ring lets go of a card once it is built. */
  function warmObserve(el) {
    if (!window.IntersectionObserver) return;
    if (!warmObserver) {
      warmObserver = new IntersectionObserver(function (entries) {
        var queued = false;
        var parked = false;
        for (var i = 0; i < entries.length; i++) {
          var qa = entries[i].target.__dwQa;
          if (!qa) continue;
          qa._near = entries[i].isIntersecting;
          if (!qa._near) continue;
          if (qa._parked) {
            qa._parked = false;
            buildQueue.push(qa);
            parked = true;
          }
          if (!qa._warmed && !qa._queued) {
            qa._queued = true;
            warmQueue.push(qa);
            queued = true;
          }
        }
        if (queued) later(warmPump);
        if (parked) later(buildPump);
      }, { rootMargin: '700px 0px 700px 0px', threshold: 0 });
    }
    if (!thumbObserver) {
      /* A second, tighter ring for the swatch thumbnails: the JSON is worth fetching a screen
         early, a dozen images per card are not. See the thumb warmer below. */
      thumbObserver = new IntersectionObserver(function (entries) {
        for (var i = 0; i < entries.length; i++) {
          var qa = entries[i].target.__dwQa;
          if (!qa) continue;
          qa._thumbNear = entries[i].isIntersecting;
          if (qa._thumbNear) qa.queueThumbs();
        }
      }, { rootMargin: '200px 0px 200px 0px', threshold: 0 });
    }
    var target = el.card || el;
    target.__dwQa = el;
    warmObserver.observe(target);
    thumbObserver.observe(target);
  }

  /* SWATCH THUMBNAILS - Pedram's second speed note, 2026-09-20: "Speed is really good, but when the
     detail comes up and I go to the colour swatches, it loads a little bit slow."
     Warming the product JSON is not enough, because the swatches are IMAGES. They carry
     loading="lazy" so a hidden panel costs nothing - and a lazy image inside display:none does not
     start loading until the panel is raised, which is exactly the beat he is seeing. The bytes are
     fetched ahead here instead, through a plain Image(), and the real <img> is flipped to eager
     once they are cached so it paints from cache instead of waiting to be scrolled into view.
     Bounded three ways, because a dozen colours across twenty warmed cards is megabytes: one
     strip's worth per card, only cards within 200px of the viewport (the JSON uses 700), and never
     before the JSON queue has drained - the JSON is what makes the panel exist at all. decode()
     finishes the work off the main thread where the browser offers it. A job whose card has left
     the 200px ring by its turn is dropped and forgotten, so the card can queue it again. */
  var THUMB_MAX = 4;
  var thumbQueue = [];
  var thumbActive = 0;
  var thumbSeen = Object.create(null);

  function thumbPump() {
    if (!warmArmed) return;
    /* Product JSON first, always. warmPump() calls this again once it has drained. */
    if (warmActive > 0 || warmQueue.length) return;
    while (thumbActive < THUMB_MAX && thumbQueue.length) {
      var job = thumbQueue.shift();
      if (!job || !job.img || !job.img.isConnected) continue;
      if (!job.force && job.el && job.el._thumbNear === false) {
        delete thumbSeen[job.src];
        continue;
      }
      thumbActive++;
      warmThumb(job);
    }
  }

  function warmThumb(job) {
    var done = function () { thumbActive--; later(thumbPump); };
    var probe = new Image();
    probe.decoding = 'async';
    probe.onload = function () {
      try { job.img.loading = 'eager'; } catch (e) {}
      if (probe.decode) { try { probe.decode().then(done, done); return; } catch (e) {} }
      done();
    };
    probe.onerror = done;
    probe.src = job.src;
  }

  /* REGRESSION B1 (critique round 1). The phone swipe took over every sideways product row: Focal's
     home rows, and PDP recommendations, are horizontal scrollers, and once a card in one carried
     touch-action: pan-y the row could no longer be dragged by its photos - on the sale theme that
     is Monday's "TEES / THE ARCHIVE" carousel. A card inside anything that scrolls sideways leaves
     the finger to the row: no pan-y, no swipe, no pending step. Asked once per container, because
     every card of a row shares the same ancestors; overflow-x auto/scroll is the test, which is what
     makes an element a sideways scroller whatever the theme calls it (the collection grid is
     overflow-x: hidden and is correctly left out). */
  var ROWS = typeof WeakMap === 'function' ? new WeakMap() : null;
  function inHorizontalScroller(el) {
    var chain = [];
    var node = el && el.parentElement;
    var found = false;
    while (node && node !== document.body && node !== document.documentElement) {
      if (ROWS && ROWS.has(node)) { found = ROWS.get(node); break; }
      chain.push(node);
      var overflow = '';
      try { overflow = window.getComputedStyle(node).overflowX; } catch (e) {}
      if (overflow === 'auto' || overflow === 'scroll') { found = true; break; }
      node = node.parentElement;
    }
    if (ROWS) for (var i = 0; i < chain.length; i++) ROWS.set(chain[i], found);
    return found;
  }

  class DwQuickAdd extends HTMLElement {
    connectedCallback() {
      if (this._ready) return;
      this.plus = this.querySelector('.dw-qa__plus');
      this.panel = this.querySelector('.dw-qa__panel');
      this.form = this.querySelector('.dw-qa__form');
      this.handle = this.getAttribute('data-handle');
      if (!this.plus || !this.panel || !this.form || !this.handle) return;
      this._ready = true;

      this.card = this.closest('product-item') || this.closest('.product-item');
      this.color = this.getAttribute('data-color') || null;
      /* REVISION 4. How many photos this card's colour has, printed by snippets/dw-quick-add.liquid
         from the same "#color_<value>" media tags photosByColor() reads - so the position bar is
         drawn at rest and the photo can take a swipe before anything has been fetched (speed B1,
         regression S6). -1 when the Liquid could not tell (a listing not split by colour). */
      var hint = parseInt(this.getAttribute('data-photos'), 10);
      this._hint = isNaN(hint) ? -1 : Math.min(hint, PHOTO_MAX);
      /* The photo the card was rendered with, read before anything can repaint it (regression S7). */
      var shown = this.card && this.card.querySelector('.product-item__primary-image:not([hidden])');
      this._cardFile = shown ? fileOf(shown.getAttribute('src')) : '';
      /* Variants that are final sale by their own custom.final_sale metafield - a clearance COLOUR
         on a product that is not in The Archive. Empty on Archive products, which are final sale as
         a whole and carry the property unconditionally from the Liquid. See add(). */
      var finalIds = this.getAttribute('data-final-ids');
      this._finalIds = finalIds ? finalIds.split(',') : [];
      this.isOpen = false;
      this._busy = false;
      uniqueFormId(this.form);

      /* A persistent live region. It deliberately sits OUTSIDE the panel, because render() replaces
         the panel's innerHTML and a live region that is inserted with its text already in it is not
         announced. This one exists from upgrade time, so writing to it after a colour change is. */
      this.live = document.createElement('span');
      this.live.className = 'dw-qa__live visually-hidden';
      this.live.setAttribute('aria-live', 'polite');
      this.appendChild(this.live);

      this.plus.addEventListener('click', this.onPlus.bind(this));
      this.plus.addEventListener('mouseenter', this.warm.bind(this));
      this.panel.addEventListener('click', this.onPanelClick.bind(this));

      /* Where the press that produced a click landed, and when. sizeTapIsOwn() weighs it against
         the last render before anything is added to the bag - the whole of Pedram's bug 1. */
      this._down = null;
      this._downAt = 0;
      this._renderAt = 0;
      var mark = this.onPress.bind(this);
      if (window.PointerEvent) {
        this.panel.addEventListener('pointerdown', mark);
      } else {
        this.panel.addEventListener('mousedown', mark);
        this.panel.addEventListener('touchstart', mark, { passive: true });
      }

      /* Hover, ask 2. Bound to the CARD, not to this element, for two reasons: the pointer should
         raise the panel from anywhere on the card the way ASRV does, and crossing from the photo
         into the panel must not read as a leave. The handlers check the media query and the
         pointer type themselves, so binding them costs a touch screen nothing. */
      hoverBind();
      var hoverTarget = this.card || this;
      this._hoverTarget = hoverTarget;
      hoverTarget.addEventListener('pointerenter', this.onEnter.bind(this));
      hoverTarget.addEventListener('pointerleave', this.onLeave.bind(this));
      hoverTarget.addEventListener('focusin', this.onFocusIn.bind(this));
      hoverTarget.addEventListener('focusout', this.onFocusOut.bind(this));

      /* ---- card image scroller, ask 2 ---- */
      this._photos = null;
      this._photo = 0;
      this._sw = null;
      this._drag = null;
      this._settle = null;
      this._swipedAt = 0;
      /* TOUCH events, not pointer events, and this was measured rather than assumed. A horizontal
         drag on the photo is arbitrated by the compositor as a scroll, so Chrome ends the pointer
         stream with pointercancel and never fires pointerup - captured on the real preview at 390:
         pointerdown(touch) -> touchstart -> pointercancel -> touchend, with no pointerup at all, so
         a pointerup-based swipe simply never fired. Touch events are delivered whatever the
         compositor decides. .dw-qa-scroll also sets touch-action: pan-y on the photo so the browser
         stops treating a sideways drag as a pan it owns, while vertical scrolling of the grid is
         untouched. Passive on purpose: nothing here calls preventDefault, so the page scrolls
         exactly as it does today. touchmove is what decides swipe-or-scroll and moves the photo
         with the finger (revision 4); touchstart only records where the finger landed. */
      hoverTarget.addEventListener('touchstart', this.onCardDown.bind(this), { passive: true });
      hoverTarget.addEventListener('touchmove', this.onCardMove.bind(this), { passive: true });
      hoverTarget.addEventListener('touchend', this.onCardUp.bind(this), { passive: true });
      hoverTarget.addEventListener('touchcancel', this.onCardCancel.bind(this), { passive: true });
      /* Capture, so a swipe that ends on the photo is swallowed before Focal's own link sees the
         click it would otherwise turn into a navigation. */
      hoverTarget.addEventListener('click', this.onCardClick.bind(this), true);

      warmObserve(this);
      /* The bar at rest, from the Liquid's count. No request, no layout shift: it takes over the
         plain rule's own 90x3 box. */
      if (this._hint > 1) this.scrollerSync();
    }

    disconnectedCallback() {
      /* A card can be removed mid-add by an AJAX filter re-render. Clear the timer and the cart
         listener with it, instead of leaving a timer to fire into detached DOM (critique QA2-S3). */
      if (this._addDone) this._addDone();
      if (this._leaveTimer) { clearTimeout(this._leaveTimer); this._leaveTimer = null; }
      if (hoverEl === this) hoverEl = null;
      if (this.card) this.card.classList.remove('dw-qa-hovering');
      /* An AJAX filter re-render drops this card; do not leave it in the warm queue holding a
         concurrency slot for a product nobody can see any more. */
      var q = warmQueue.indexOf(this);
      if (q > -1) warmQueue.splice(q, 1);
      var b = buildQueue.indexOf(this);
      if (b > -1) buildQueue.splice(b, 1);
      this._product = null;
      this._queued = false;
      if (this._hoverTarget) {
        /* Both rings, not only the JSON one (regression S10): the thumbnail observer was holding
           every card an AJAX filter re-render had thrown away. */
        try { if (warmObserver) warmObserver.unobserve(this._hoverTarget); } catch (e) {}
        try { if (thumbObserver) thumbObserver.unobserve(this._hoverTarget); } catch (e) {}
      }
      clearTimeout(this._settleTimer);
      clearTimeout(this._navDwell);
      this._drag = null;
      this._settle = null;
      if (this.isOpen) this.close(false);
    }

    /* Fetch this card's product and, when it lands, build its panel - so a card the shopper reaches
       before the queue got to it still ends up warm rather than only fetched. */
    warm() {
      var self = this;
      this._warmed = true;
      var request;
      try { request = fetchProduct(this.handle); } catch (e) { return; }
      /* Built straight away, not through the idle queue: the shopper is pointing at this one. */
      request.then(function (product) {
        try { self.prebuild(product, request.at); } catch (e) {}
      }, function () {
        self._warmed = false;
      });
    }

    /* Everything open() used to do on its first run, done at idle instead: the model, the snapshot,
       the panel's DOM and the scroller's photo list. After this a hover is a class and an attribute,
       which is why a warm card paints in single-figure milliseconds instead of ninety.
       Deliberately does nothing once the card is open or already built, and deliberately does NOT
       repaint the card: build()'s first-render repaint is skipped while _prebuilding, so a card
       nobody has pointed at is never moved off the colour the server rendered. */
    prebuild(product, at) {
      if (!this._ready || this._built || this.isOpen || this._busy) return;
      if (!this.model) {
        if (!product) return;
        this.model = modelOf(product);
        this._modelAt = at || 0;
      }
      var current = this.currentColor();
      if (!current) return;
      this._prebuilding = true;
      try {
        this.useColor(current.value);
        this.snapshot();
        this.build(current);
      } finally {
        this._prebuilding = false;
      }
      this._renderAt = now();
      /* Built: the JSON ring has nothing more to do for this card. */
      if (warmObserver && this._hoverTarget) {
        try { warmObserver.unobserve(this._hoverTarget); } catch (e) {}
      }
      /* A swipe made while this card's photo list was still in flight. */
      this.flushPending();
    }

    onPlus(event) {
      event.preventDefault();
      event.stopPropagation();
      if (this.isOpen) this.close(true);
      else this.open();
    }

    open(viaHover) {
      /* CRITIQUE QA3-S4. Closing the card that was open sets panel.hidden, and a chevron or swatch
         focused inside a subtree that has just been hidden is blurred by the browser: a keyboard
         shopper whose mouse happened to move lost their place and the next Tab restarted at the top
         of a 109-card grid (measured: focus went BUTTON.dw-qa__arrow -> BODY). Hand the focus back
         to that card's own "+" instead of dropping it on <body>. close(true) does exactly that, and
         onFocusIn ignores a focus that lands on a ".dw-qa__plus", so nothing re-opens behind us. */
      if (openEl && openEl !== this) {
        var hadFocus = !!(openEl.panel && openEl.panel.contains(document.activeElement));
        openEl.close(hadFocus);
      }
      /* CRITIQUE S2. A hover is not a choice. render()'s first-render repaint is DEFERRED while the
         panel was raised by the pointer alone, so moving the mouse across the grid can never change
         a card's photo, price line or links - the spec's "one 160ms ease for the panel, nothing
         else moves". The moment the shopper actually uses the panel, onPanelClick clears this and
         the repaint runs, so card and panel still agree before anything can be added. */
      this._openedByHover = !!viaHover;
      learnPage();
      openEl = this;
      this.isOpen = true;
      /* A new open is a clean slate: the "could not add" line from a previous attempt is not a fact
         about this one. */
      this._failed = false;
      /* A press recorded before this panel was open can never justify an add inside it. */
      this._down = null;
      this._downAt = 0;
      /* A hook for the CSS, and it earns its keep: see the secondary-image rule in
         assets/dw-quick-add.css. Kept off :has() so it works in every browser the store sees. */
      if (this.card) this.card.classList.add('dw-qa-open');
      if (this._leaveTimer) { clearTimeout(this._leaveTimer); this._leaveTimer = null; }
      this.plus.setAttribute('aria-expanded', 'true');
      this.panel.hidden = false;
      var panel = this.panel;
      requestAnimationFrame(function () { panel.classList.add('is-open'); });

      this._outside = this.onOutside.bind(this);
      document.addEventListener('click', this._outside, true);
      document.addEventListener('touchstart', this._outside, true);
      /* Escape is bound to the document, not to this element: render() replaces the panel's
         contents, so after a colour change the focused swatch is destroyed and focus falls to
         <body>. An element-level listener never sees the key then (critique QA-2). */
      this._esc = this.onKeydown.bind(this);
      document.addEventListener('keydown', this._esc);

      /* A card the queue already warmed goes straight up: the panel's DOM is there, the model is
         there, and render() only has to move the selected mark and repaint the chips in place.
         No skeleton is ever drawn on a warm card, which is the spec's "no visible skeleton". */
      if (this.model) {
        this.render();
        this.refreshIfStale();
        return;
      }
      warmArm();
      this.panel.innerHTML = '<div class="dw-qa__skeleton"></div><div class="dw-qa__skeleton"></div>';
      /* The skeleton has just thrown the built panel away, so the next render must build, not
         update. Same for the error branch below. */
      this._built = false;
      this.measure();
      var self = this;
      var request = fetchProduct(this.handle);
      request.then(function (product) {
        if (!self.model) {
          self.model = modelOf(product);
          self._modelAt = request.at;
        }
        if (self.isOpen) self.render();
      }).catch(function () {
        if (!self.isOpen) return;
        self.panel.innerHTML = '<p class="dw-qa__error">' + esc(str('optionsError')) + ' ' +
          '<a href="' + esc(root() + 'products/' + self.handle) + '">' + esc(str('openProduct')) + '</a></p>';
        self._built = false;
        self.measure();
      });
    }

    close(returnFocus) {
      this.isOpen = false;
      if (openEl === this) openEl = null;
      /* Put the card back exactly as the server rendered it. Without this, a colour tapped in the
         panel stayed painted on the card after the panel shut - and on a colour-split grid, where
         every colour of a product is its own card, the grid then showed the same colour twice and
         the card's own colour not at all until a reload (critique R2-B2). The panel is a chooser,
         not an edit to the grid: what it changes lives only as long as it is open. */
      this.restoreCard();
      /* The card goes back to its own colour, because previewing a colour in the panel is not a
         choice about the grid (critique R2-B2). Moving through the PHOTOS is a different thing: the
         shopper clicked an arrow or swiped, deliberately, so that survives the panel closing - but
         only while the colour is unchanged, or the bar would be counting one colour's photos over
         another colour's picture. restoreCard() has just put the server's <img> back, so photo one
         needs nothing more and any later photo is re-applied. */
      if (this._snap) {
        if (this.color !== this._snap.color) {
          this.useColor(this._snap.color);
          this._photo = 0;
        }
        this.scrollerSync();
        if (this._photo > 0) this.showPhoto();
      }
      this.plus.setAttribute('aria-expanded', 'false');
      this.panel.classList.remove('is-open');
      this.panel.hidden = true;
      this._keyOpen = false;
      if (this.card) this.card.classList.remove('dw-qa-open');
      if (this._leaveTimer) { clearTimeout(this._leaveTimer); this._leaveTimer = null; }
      if (this._outside) {
        document.removeEventListener('click', this._outside, true);
        document.removeEventListener('touchstart', this._outside, true);
        this._outside = null;
      }
      if (this._esc) {
        document.removeEventListener('keydown', this._esc);
        this._esc = null;
      }
      /* A refresh that changed the colour list itself waited for the panel to close (applyModel). */
      if (this._nextModel) {
        this.model = this._nextModel;
        this._nextModel = null;
        this._finalAll = null;
        this._built = false;
      }
      if (returnFocus) this.plus.focus();
    }

    /* REGRESSION S2 (critique round 1). Revision 3 built warm cards at idle and open() then redrew
       that model forever, so the 90s TTL and the drop-on-refused-add (QA4-S1) no longer reached any
       warmed card - and the Archive's stock is thin enough that a grid left open on Monday would
       keep offering sizes that had gone. Now: the warm model is drawn at once (the speed gain is
       kept), and if it is older than the cache allows - or the cache has moved on, or a refused add
       dropped it - the product is re-read in the background. If anything sold, the chips and the
       swatches are repainted IN PLACE: no skeleton, no rebuild, the panel's height unchanged, and
       _renderAt moves so sizeTapIsOwn() refuses a press that began before the repaint. */
    refreshIfStale() {
      var entry = CACHE.get(this.handle);
      var fresh = entry && (now() - entry.at) <= CACHE_TTL;
      if (fresh && entry.at === this._modelAt) return;
      var self = this;
      var request = fetchProduct(this.handle);
      request.then(function (product) {
        self._modelAt = request.at;
        var next = modelOf(product);
        if (!self.model || next === self.model) return;
        if (stockKey(next) === stockKey(self.model)) {
          self.model = next;
          return;
        }
        self.applyModel(next);
      }, function () {});
    }

    applyModel(next) {
      var prev = this.model;
      var same = prev.colors.length === next.colors.length;
      for (var i = 0; same && i < next.colors.length; i++) {
        if (prev.colors[i].value !== next.colors[i].value) same = false;
      }
      if (!same) {
        /* A colour added or removed while the page was open - rare enough that the panel is not
           rebuilt under the shopper; it is rebuilt on the next open instead. */
        if (this.isOpen) { this._nextModel = next; return; }
        this.model = next;
        this._finalAll = null;
        this._built = false;
        return;
      }
      this.model = next;
      this._finalAll = null;
      if (!this.isOpen || !this._built) return;
      var current = this.currentColor();
      if (!current) return;
      this.update(current);
      this._renderAt = now();
      this.measure();
    }

    onOutside(event) {
      if (this.contains(event.target)) return;
      /* The photo arrows are mounted on the image wrapper, not inside <dw-quick-add>, so without
         this a click on one counted as a click outside and shut the panel - measured at 1024 and
         1440, where every arrow click dropped the panel the shopper was in the middle of using.
         Ask 2 says the scroller must not fight the panel; this is that, literally. */
      if (this.nav && this.nav.contains(event.target)) return;
      /* Remember that THIS touch was a dismissal. It is delivered here, on the document, in the
         capture phase - before the card's own touchstart listener - so without this the same finger
         that closed the panel would also start a swipe and move the photo as a side effect. */
      this._dismissAt = now();
      this.close(false);
    }

    onKeydown(event) {
      if (event.key === 'Escape' && this.isOpen) {
        event.stopPropagation();
        this.close(true);
      }
    }

    /* ---- hover (ask 2) ---------------------------------------------------------------------- */

    onEnter(event) {
      if (!isMouse(event) || !hoverCapable()) return;
      /* CRITIQUE SF7. Focal's secondary-image swap used to start on pointerenter and snap back
         ~40ms later when .dw-qa-open landed (sampled: primary 0.944 / secondary 0.056 at t+25ms),
         so every card the pointer touched flashed a partial cross-fade before the panel rose. The
         hold is keyed to the HOVER now, not to the open, so the photo is still from the first
         frame. Added only behind the same mouse + (hover:hover) gate as the open itself, so a touch
         screen keeps Focal's behaviour exactly as it is today. */
      if (this.card) this.card.classList.add('dw-qa-hovering');
      hoverEl = this;
      if (this._leaveTimer) { clearTimeout(this._leaveTimer); this._leaveTimer = null; }
      /* Belt and braces on the speed ask: if the queue has not reached this card yet, hovering it
         still fetches and builds it immediately, exactly as it always did. */
      warmArm();
      /* Not "!_warmed": a card whose JSON has landed but whose turn in the build queue has not come
         yet is warmed and still has no panel. warm() hands back the cached product in a microtask,
         so the panel is built before the open timer fires and no skeleton is drawn. */
      if (!this.model) this.warm();
      /* The pointer is on this card now, so its swatches are about to be looked at. */
      this.queueThumbs(true);
      armOpen();
    }

    onLeave(event) {
      if (!isMouse(event)) return;
      if (this.card) this.card.classList.remove('dw-qa-hovering');
      if (hoverEl === this) hoverEl = null;
      if (hoverTimer) { clearTimeout(hoverTimer); hoverTimer = null; }
      if (!this.isOpen || this._keyOpen) return;
      var self = this;
      if (this._leaveTimer) clearTimeout(this._leaveTimer);
      /* The drop is deliberately slower than the rise. A pointer that clips this card's corner on
         its way to the next one leaves and re-enters within a frame or two, and a symmetric timer
         made that read as a blink. By the time this fires, the next card's open() has already
         closed this one, so on a normal sweep across a row it finds nothing to do. */
      this._leaveTimer = setTimeout(function () {
        self._leaveTimer = null;
        if (hoverEl === self || self._busy || self._keyOpen || !self.isOpen) return;
        /* Someone is IN here. A shopper who hovered a card and then tabbed into the strip would
           otherwise have the panel pulled out from under them the moment the mouse drifted off,
           and focus would fall to <body>. Escape, a click outside, or opening another card all
           still close it.
           FOUND IN THE RE-TEST, 2026-09-20: this guard read a MOUSE click as "someone is in here".
           Clicking a colour focuses that swatch, so after one click the panel stopped obeying
           mouse-leave: it stayed up with the clicked colour painted on the card, and because the
           card is only restored by close(), the grid kept showing a colour the shopper had only
           been previewing (measured on wrist-wrap-2-stripe and power-play-oversized-shirt at 1440:
           panel still open and the card still repainted a second after the pointer had gone).
           Ask 2 says the panel drops when the mouse leaves, so only a KEYBOARD focus holds it now -
           :focus-visible, the same test onFocusIn uses. A mouse click leaves focus behind on a
           control nobody is navigating with, so it is dropped deliberately here rather than being
           blurred by the hidden attribute a moment later (critique QA3-S4, same failure). */
        if (self.panel.contains(document.activeElement)) {
          var held = true;
          try { held = document.activeElement.matches(':focus-visible'); } catch (e) { held = true; }
          if (held) return;
          try { document.activeElement.blur(); } catch (e) {}
        }
        self.close(false);
      }, HOVER_OUT);
    }

    /* Keyboard gets the same behaviour as the pointer: arriving on the card opens the panel, and
       leaving it closes it. Two things are deliberately excluded - the "+" itself, because
       pressing it is what opens the panel and opening on focus as well would make one press
       toggle twice, and anything already inside an open panel. :focus-visible keeps this off a
       mouse click that happens to focus something. */
    onFocusIn(event) {
      if (!hoverCapable() || this.isOpen) return;
      var target = event.target;
      if (!target || !target.closest) return;
      if (target.closest('.dw-qa__plus') || target.closest('.dw-qa__panel')) return;
      var visible = true;
      try { visible = target.matches(':focus-visible'); } catch (e) { visible = true; }
      if (!visible) return;
      this._keyOpen = true;
      this.open();
    }

    onFocusOut() {
      if (!this._keyOpen) return;
      var self = this;
      /* relatedTarget is null while focus is in flight, so settle on the next tick and ask the
         document where focus actually landed. */
      setTimeout(function () {
        var target = self._hoverTarget || self;
        if (target.contains && target.contains(document.activeElement)) return;
        self._keyOpen = false;
        if (self.isOpen && !self._busy && hoverEl !== self) self.close(false);
      }, 0);
    }

    /* ---- one tap, one thing (ask 1) ---------------------------------------------------------- */

    onPress(event) {
      var target = event.target && event.target.closest ? event.target.closest('.dw-qa__size') : null;
      this._down = target;
      this._downAt = now();
    }

    /* THE BUG PEDRAM REPORTED on 2026-09-20: "when I select one colour and choose one size, and
       then I want to select another colour, instead of picking that colour it goes straight to add
       to cart."
       One tap was able to do two things. A swatch tap re-rendered the panel synchronously inside
       the click, and the panel is anchored to the BOTTOM of the image, so anything in it that grew
       or shrank - the sold-out line that used to sit under the size row, the clearance line, the
       arrow column appearing as the strip scrolled - moved the size chips vertically, under a
       finger that had not lifted. The next contact at the same spot was an add nobody aimed at.
       Two changes close it, and they are independent on purpose.
       Structural, in build()/update(): the panel is never replaced wholesale again, the swatch
       strip is not rebuilt at all, and the size row is repainted in place and is now the LAST
       thing in the panel, so in an anchored-to-the-bottom panel its position cannot move. Nothing
       shifts under the finger in the first place.
       Categorical, here: an add requires that the press which produced this click landed on THIS
       chip, and landed after the last render. A tap that began on a swatch can never finish as an
       add, whatever the layout did in between - which is what "one tap can only ever do one thing"
       has to mean if it is to survive the next layout change too.
       Keyboard is not slowed down at all: Enter and Space on a focused chip arrive with detail 0
       and no press behind them, and go straight through. */
    sizeTapIsOwn(event, size) {
      if (!event.detail) return true;
      if (this._downAt) return this._down === size && this._downAt >= this._renderAt;
      return now() - this._renderAt > REBUILD_GUARD;
    }

    /* Tell the CSS how tall the panel is, so the "+" can sit clear above it while open instead of
       being buried underneath it. Before this, the panel covered the disc completely: the toggle
       could not be tapped to close, and a tap aimed at it landed on a size button (critique QA-5 /
       S2). */
    measure() {
      var height = this.panel.offsetHeight;
      if (height > 0) this.style.setProperty('--dw-qa-panel-h', height + 'px');
    }

    currentColor() {
      var colors = this.model.colors;
      if (!colors.length) return null;
      var wanted = this.color;
      var i;
      if (wanted != null) {
        for (i = 0; i < colors.length; i++) {
          if (colors[i].value === wanted) return colors[i];
        }
      }
      var variantId = this.getAttribute('data-variant');
      if (variantId) {
        for (i = 0; i < colors.length; i++) {
          for (var j = 0; j < colors[i].sizes.length; j++) {
            if (String(colors[i].sizes[j].id) === String(variantId)) return colors[i];
          }
        }
      }
      /* Listings whose colour option is not named "Color" (Wrist-wrap-color, Knee-wrap-color) are not
         split into one card per colour, so the card carries no colour or variant to match on. Read it
         off the photo the card is actually showing instead of assuming the first colour. */
      var image = this.card && this.card.querySelector('.product-item__primary-image:not([hidden])');
      var source = image && (image.currentSrc || image.src);
      if (source) {
        var file = source.split('?')[0].split('/').pop();
        for (i = 0; i < colors.length; i++) {
          if (colors[i].image && colors[i].image.split('?')[0].split('/').pop() === file) {
            /* CRITIQUE S4 (edge-data). Record it, so snapshot() can treat a card whose photo
               identifies its colour the same as a colour-split card that says so in an attribute.
               Without this, every listing whose colour option is not literally named "Color" - all
               the wrist wraps, knee wraps, D Straps, rings - took the repaint branch on first open,
               which stripped Focal's hover-to-second-image class and rewrote the price line and both
               links before the shopper had chosen anything. */
            this._photoColor = colors[i].value;
            return colors[i];
          }
        }
      }
      return colors[0];
    }

    render() {
      var current = this.currentColor();
      if (!current) { this.close(false); return; }
      this.useColor(current.value);
      /* Taken on the FIRST render only, while the card still shows exactly what the server sent,
         so coming back to this card's own colour restores it instead of leaving a near-miss. */
      this.snapshot();

      if (this._built) this.update(current);
      else this.build(current);

      /* The swatch strip's arrows and its scroll position can only be worked out against a panel
         that has a width, and a panel built at idle was display:none. Do it here, once, the first
         time the panel is actually up. On a cold open the panel is already visible by the time
         build() runs, so this fires in the same tick it always did. */
      if (this._needsLayout && !this.panel.hidden) {
        this._needsLayout = false;
        this.layoutStrip();
      }

      /* The moment the panel last changed shape. sizeTapIsOwn() refuses any add whose press began
         before this instant. */
      this._renderAt = now();
      this.measure();
    }

    /* Does the colour on screen have a variant the Liquid flagged with custom.final_sale? Driven by
       exactly the ids add() checks before it enables the properties[Final sale] input, so the card
       never records a restriction it did not show first. */
    isFinal(current) {
      if (!this._finalIds.length) return false;
      for (var i = 0; i < current.sizes.length; i++) {
        if (this._finalIds.indexOf(String(current.sizes[i].id)) !== -1) return true;
      }
      return false;
    }

    /* CEO PASS, 2026-09-20: on an Archive product, where the WHOLE product is final sale, the
       wording "Clearance color - final sale" read as though one colour had been singled out.
       The two cases are already distinguishable from data this card is holding: snippets/
       dw-quick-add.liquid prints EVERY variant id into data-final-ids when the product is in The
       Archive, and only the ids carrying custom.final_sale otherwise. So "is every variant of this
       product final sale?" is the question, and it is answered here rather than by a new attribute.
       Worked out once - the model does not change for the life of a card. */
    isFinalProduct() {
      if (this._finalAll != null) return this._finalAll;
      this._finalAll = false;
      if (this._finalIds.length && this.model) {
        var colors = this.model.colors, i, j;
        var all = true;
        for (i = 0; i < colors.length && all; i++) {
          for (j = 0; j < colors[i].sizes.length; j++) {
            if (this._finalIds.indexOf(String(colors[i].sizes[j].id)) === -1) { all = false; break; }
          }
        }
        this._finalAll = all;
      }
      return this._finalAll;
    }

    /* Does this card ever have anything to say in its status line? Asked once, off the whole model,
       because the answer decides whether the line is RESERVED at all.
       This is what keeps blocker B2 closed while moving the line back inside the panel. The panel is
       anchored to the bottom of the photo, so anything in its flow that appears or collapses moves
       the swatch strip under the shopper's finger - the root of Pedram's one-tap bug. The line is
       therefore never shown-and-hidden: on a card that can ever need it, it is present from the
       first build at a fixed one-line height and only its TEXT changes; on a card that can never
       need it - most of the store - it is not rendered at all and costs nothing. Either way the
       panel's height is constant for the life of the card, which is all that guarantee requires. */
    needsStatus() {
      if (this._finalIds.length) return true;
      var colors = this.model.colors;
      for (var i = 0; i < colors.length; i++) {
        if (!colors[i].available) return true;
      }
      return false;
    }

    /* One line, never two. A sold-out colour has nothing to sell, so the clearance terms are moot
       and the shopper is told the thing that matters instead; that is also what keeps the line
       inside its fixed height at 360px. */
    statusText(current) {
      if (this._failed) return str('addError');
      if (!current.available) return str('soldOut');
      if (this.isFinal(current)) {
        return this.isFinalProduct() ? str('finalSaleProduct') : str('finalSale');
      }
      return '';
    }

    /* One size chip as HTML. Shared by the first build and by fillSizes()'s rebuild path so a chip
       is the same thing however it got there. */
    chipHTML(size) {
      var named = this.model.hasSizes && size.label;
      var label = named ? this.chipLabel(size.label) : str('addLabel');
      var only = named ? '' : ' dw-qa__size--only';
      if (size.available) {
        return '<button type="button" class="dw-qa__size' + only + '" data-id="' + esc(size.id) + '">' +
          esc(label) + '</button>';
      }
      return '<button type="button" class="dw-qa__size is-out' + only + '" disabled aria-disabled="true">' +
        esc(label) + '<span class="visually-hidden"> ' + esc(str('soldOut')) + '</span></button>';
    }

    /* Track width follows the longest label in this row. Sizes are two or three characters and want
       narrow chips; a gift card's row is "$50.00" ... "$200.00" and was clipping mid-glyph in a
       size-shaped chip, and "XXXL" clipped by a hair at 360. Three widths, chosen by the longest
       label, rather than one width that suits neither.
       The no-size shape renders one "Add" chip; it is a primary button, not a size, so the row opts
       out of the capped track and keeps the full panel width (critique R3-S2). */
    sizesClass(current) {
      var model = this.model;
      if (!model.hasSizes) return 'dw-qa__sizes dw-qa__sizes--single';
      /* REVISION 4 (scroller B1): every clothing-size row is ONE width, whatever its labels. The
         --mid width revision 3 gave to rows containing "XXXL" made arc-define-t-shirt's chips 46px
         next to 40px ones on the same grid; chipText() draws the short form instead. The wider
         tracks survive only for rows that are not sizes. */
      if (model.sizeish) return 'dw-qa__sizes';
      var longest = 0;
      current.sizes.forEach(function (size) {
        var text = size.label ? String(size.label) : '';
        if (text.length > longest) longest = text.length;
      });
      var width = longest >= 5 ? ' dw-qa__sizes--wide' : (longest === 4 ? ' dw-qa__sizes--mid' : '');
      return 'dw-qa__sizes' + width;
    }

    /* What a chip draws: the short size form on a size row (chipText), the label as it is anywhere
       else. */
    chipLabel(label) {
      return this.model.sizeish ? chipText(label) : label;
    }

    /* The first open of a card: the whole panel, once. */
    build(current) {
      var model = this.model;
      var html = '';
      if (model.colors.length > 1) {
        /* SPEED: the selected colour's thumbnail is the photo the card is ALREADY showing, so it is
           asked for by the exact url the browser has already decoded rather than by a new ?width=.
           That is one fewer request on every open, and it is the thumb the eye goes to first, so
           the strip never opens with its selected square still blank. The rest stay lazy: inside a
           panel that is display:none they cost nothing until it is raised, and width/height below
           means they cannot shift the strip when they land. */
        var cardImg = this.card && this.card.querySelector('.product-item__primary-image:not([hidden])');
        var cardSrc = (cardImg && (cardImg.currentSrc || cardImg.getAttribute('src'))) || '';
        var cardFile = cardSrc ? fileOf(cardSrc) : '';
        html += '<div class="dw-qa__colors">' +
          '<button type="button" class="dw-qa__arrow dw-qa__arrow--prev" hidden aria-label="' +
          esc(str('previousColors')) + '">' + chevron('prev') +
          '</button><div class="dw-qa__track" role="group" aria-label="' +
          esc(model.colorLabel) + '">';
        model.colors.forEach(function (group) {
          var selected = group === current;
          var onCard = selected && cardFile && group.image && fileOf(group.image) === cardFile;
          var thumb = onCard ? cardSrc : sized(group.image, SWATCH_PX);
          html += '<button type="button" class="dw-qa__sw' + (selected ? ' is-selected' : '') +
            (group.available ? '' : ' is-out') + '" data-color="' + esc(group.value) + '" aria-pressed="' +
            (selected ? 'true' : 'false') + '"><img src="' + esc(thumb) +
            '" alt="" loading="' + (onCard ? 'eager' : 'lazy') + '" decoding="async" width="44" height="44">' +
            '<span class="visually-hidden">' +
            esc(group.value) + (group.available ? '' : ' (' + esc(str('soldOut')) + ')') + '</span></button>';
        });
        html += '</div><button type="button" class="dw-qa__arrow dw-qa__arrow--next" hidden aria-label="' +
          esc(str('moreColors')) + '">' + chevron('next') + '</button></div>';
      }

      /* SHAPE, revised 2026-09-20 after the critique (blocker B2), and it is load-bearing.
         The clearance line and the message sit above the size row, and they are now in a slot that
         is taken OUT of the panel's flow (position:absolute; bottom:100% - see .dw-qa__status in
         assets/dw-quick-add.css). The first version simply put them above the chips, which pinned
         the SIZE ROW (measured 0px across every colour change) but not the SWATCH STRIP: the panel
         is anchored to the bottom of the image, so a clearance or sold-out line appearing or
         collapsing changed the panel's height and slid the strip 14-26px under the finger. A second
         tap at the same place then hit dead panel, or on the sale theme a DIFFERENT swatch - which
         is Pedram's own complaint, converted from a wrong add into a wrong colour. Out of flow, the
         panel's height never changes on a colour change, so NOTHING inside it moves. Part one of
         the fix for his bug 1; sizeTapIsOwn() is part two. */
      /* SHAPE, revised again 2026-09-20 for the CEO pass. The status line is back INSIDE the panel,
         between the swatch strip and the size chips - the last thing read before a size is tapped -
         and the absolutely-positioned white box that used to sit above the panel over the photo is
         gone. That box was the loudest thing on an open Archive card at 360 and 390 (up to 76px
         tall, 10px bold red caps wrapping to three lines).
         Blocker B2 is still closed, by reservation rather than by taking the line out of the flow:
         needsStatus() decides ONCE, off the whole model, whether this card can ever have something
         to say. If it can, the row is present from this build at a fixed one-line height and only
         its text changes. If it cannot - most of the store - it is not rendered. Either way the
         panel's height never changes for the life of the card, so nothing moves under a finger.
         Part one of the fix for Pedram's one-tap bug; sizeTapIsOwn() is part two. */
      /* --final: a card that can show the clearance wording reserves TWO lines on a phone, where the
         product page's words do not fit one (regression S1). Decided once, like the row itself. */
      if (this.needsStatus()) {
        html += '<p class="dw-qa__status' + (this._finalIds.length ? ' dw-qa__status--final' : '') + '"></p>';
      }
      html += '<div class="' + this.sizesClass(current) + '" role="group" aria-label="' +
        esc(model.sizeLabel) + '"></div>';

      this.panel.innerHTML = html;
      this.panel.setAttribute('aria-label', (model.title || 'Product') + ' options');
      this._built = true;

      this.statusLine = this.panel.querySelector('.dw-qa__status');
      this.sizeRow = this.panel.querySelector('.dw-qa__sizes');
      this.track = this.panel.querySelector('.dw-qa__track');

      if (this.track) this.track.addEventListener('scroll', this.syncArrows.bind(this), { passive: true });
      /* Deferred, because a panel built at idle is display:none and every width it would measure is
         zero. render() runs it the first time the panel is actually up. */
      this._needsLayout = true;

      this.fillSizes(current);
      this.paintLines(current);
      this.scrollerSync();
      /* The strip exists now, so its first screenful of thumbnails can start warming - gated inside
         queueThumbs() on this card being near the viewport. */
      this.queueThumbs();

      /* CRITIQUE R4-S1. On a listing the Liquid could not colour-split (the colour option is not
         literally named "Color", e.g. "Wrist-wrap-color"), currentColor() falls through to the
         first colour, which is not necessarily the one the card photo shows. Measured on
         /search?q=wrap: the 4-stripe card showed one colour and the panel preselected another, so a
         shopper who tapped a size without reading the strip added a colour they were never shown.
         Repainting the card once, on the first render, makes the two agree. On a colour-split card
         the snapshot colour IS the current colour, so paintCard() short-circuits to restoreCard()
         and nothing moves; selectColor() keeps doing its own repaint after that.
         _prebuilding is in the guard for the same reason _openedByHover is: warming a card at idle
         is not a shopper choosing anything, and a grid that repainted itself while nobody touched
         it would be the opposite of what this feature is for. The flag is not set, so the first
         real open still repaints. */
      if (!this._firstRendered && !this._openedByHover && !this._prebuilding) {
        this._firstRendered = true;
        this.paintCard();
      }
    }

    /* One strip's worth of swatch thumbnails, queued for the warmer - a screenful plus one, the
       rest left to load as the strip is scrolled. Skipped while the card is far from the viewport;
       the thumb observer calls this again when it gets close, and onEnter() when the pointer
       arrives. 49px = a 44px swatch plus its 5px gap. */
    queueThumbs(force) {
      if (!warmArmed || !this.track || !this.card) return;
      /* Where the card is comes from the thumbnail observer now (revision 4, speed S2), not from a
         getBoundingClientRect() per card - a forced layout inside every idle build. force: the
         pointer is on this card, so it is near by definition. */
      if (!force && this._thumbNear === false) return;
      var imgs = this.track.querySelectorAll('img');
      if (!imgs.length) return;
      var width = this.card.offsetWidth;
      if (!width) return;
      var per = Math.ceil(width / 49) + 1;
      if (per > 8) per = 8;
      /* SPEED S4 (critique round 1): the strip opens CENTRED on the selected colour (layoutStrip),
         so the window warmed is the one around it. Revision 3 always warmed swatches 0-7; the Pink
         card of power-play-oversized-shirt (colour 9 of 9) opens on swatches 5-10 and painted them
         at 74ms against 22-26ms on a typical warm card. */
      var selected = 0;
      var swatches = this.track.children;
      for (var k = 0; k < swatches.length; k++) {
        if (swatches[k].classList.contains('is-selected')) { selected = k; break; }
      }
      var start = Math.max(0, selected - Math.floor(per / 2));
      var end = Math.min(imgs.length, start + per);
      start = Math.max(0, end - per);
      var queued = false;
      for (var i = start; i < end; i++) {
        var src = imgs[i].getAttribute('src');
        if (!src || thumbSeen[src]) continue;
        thumbSeen[src] = true;
        thumbQueue.push({ img: imgs[i], src: src, el: this, force: !!force });
        queued = true;
      }
      if (queued) later(thumbPump);
    }

    /* The swatch strip's arrows and its starting scroll position. Split out of build() so it can be
       run later than build() on a panel that was put together at idle. */
    layoutStrip() {
      if (!this.track) return;
      /* Show or hide the arrows FIRST. They start hidden, so the track is ~40px wider than it
         will end up; scrolling before they appear computes against the wrong width and the
         selected swatch lands just off the right edge. */
      this.syncArrows();
      var selectedSwatch = this.panel.querySelector('.dw-qa__sw.is-selected');
      if (!selectedSwatch) return;
      /* Measured from the rects, not offsetLeft: the track is not a positioned ancestor, so
         offsetLeft would be relative to the card's image wrapper.
         Only ever done HERE, once. Re-centring after every colour change scrolled the strip
         sideways under the finger that had just tapped it, so the next tap at the same spot landed
         on a different colour. */
      this.track.scrollLeft += selectedSwatch.getBoundingClientRect().left -
        this.track.getBoundingClientRect().left -
        (this.track.clientWidth - selectedSwatch.offsetWidth) / 2;
      this.syncArrows();
    }

    /* Every later render - a colour change, or re-opening a card that has been open before.
       Nothing is thrown away. The swatch strip is not rebuilt at all, because it is the same strip
       for every colour of a product and only the mark on it moves: the button under the finger
       keeps its node, its focus and the strip's scroll position. The size chips are repainted in
       place whenever the count is the same, which it is for every colour of every garment on this
       store. */
    update(current) {
      var swatches = this.panel.querySelectorAll('.dw-qa__sw');
      /* A stock refresh (refreshIfStale) can take a colour's last size away, so the swatch's faded
         state and its spoken "(Sold out)" follow the model too, in place. */
      var groups = Object.create(null);
      this.model.colors.forEach(function (group) { groups[group.value] = group; });
      for (var i = 0; i < swatches.length; i++) {
        var value = swatches[i].getAttribute('data-color');
        var on = value === current.value;
        swatches[i].classList.toggle('is-selected', on);
        swatches[i].setAttribute('aria-pressed', on ? 'true' : 'false');
        var group = groups[value];
        if (group) {
          swatches[i].classList.toggle('is-out', !group.available);
          var spoken = swatches[i].querySelector('.visually-hidden');
          var words = group.value + (group.available ? '' : ' (' + str('soldOut') + ')');
          if (spoken && spoken.textContent !== words) spoken.textContent = words;
        }
      }
      if (this.sizeRow) this.sizeRow.className = this.sizesClass(current);
      this.fillSizes(current);
      this.paintLines(current);
      this.scrollerSync();
    }

    fillSizes(current) {
      var row = this.sizeRow;
      if (!row) return;
      var sizes = current.sizes;
      var kids = row.children;
      var i;
      if (kids.length !== sizes.length) {
        var html = '';
        for (i = 0; i < sizes.length; i++) html += this.chipHTML(sizes[i]);
        row.innerHTML = html;
        return;
      }
      for (i = 0; i < sizes.length; i++) {
        var size = sizes[i];
        var chip = kids[i];
        var named = this.model.hasSizes && size.label;
        var label = named ? this.chipLabel(size.label) : str('addLabel');
        chip.className = 'dw-qa__size' + (size.available ? '' : ' is-out') +
          (named ? '' : ' dw-qa__size--only');
        chip.removeAttribute('aria-busy');
        chip.textContent = label;
        if (size.available) {
          chip.disabled = false;
          chip.removeAttribute('aria-disabled');
          chip.setAttribute('data-id', String(size.id));
        } else {
          chip.disabled = true;
          chip.setAttribute('aria-disabled', 'true');
          /* Nothing to add, so nothing to carry an id: a stale one here would be a variant the
             shopper cannot see. */
          chip.removeAttribute('data-id');
          var hidden = document.createElement('span');
          hidden.className = 'visually-hidden';
          hidden.textContent = ' ' + str('soldOut');
          chip.appendChild(hidden);
        }
      }
    }

    /* The two lines above the size row.
       The clearance disclosure is shown for the colour on screen before any size can be tapped, in
       the store's own wording, and exactly when add() will enable the properties[Final sale] input.
       The message says so when a colour has nothing left to sell, instead of leaving a row of
       struck-through chips with no explanation (critique QA-3). */
    paintLines(current) {
      if (!this.statusLine) return;
      var text = this.statusText(current);
      if (this.statusLine.textContent !== text) this.statusLine.textContent = text;
    }

    /* Repaint the status line for whatever colour is on screen. add() calls it after it sets or
       clears _failed, which is the only path that writes the line from outside paintLines(). */
    syncStatus() {
      if (!this.statusLine || !this.model) return;
      var current = this.currentColor();
      if (current) this.paintLines(current);
    }

    syncArrows() {
      if (!this.track) return;
      var prev = this.panel.querySelector('.dw-qa__arrow--prev');
      var next = this.panel.querySelector('.dw-qa__arrow--next');
      if (!prev || !next) return;
      var overflow = this.track.scrollWidth - this.track.clientWidth;
      if (overflow <= 2) {
        prev.hidden = true;
        next.hidden = true;
        /* No overflow means no arrow column, so the size row below takes the full width
           (critique R3-S1). */
        this.panel.classList.remove('dw-qa--arrowed');
        return;
      }
      this.panel.classList.add('dw-qa--arrowed');
      prev.hidden = this.track.scrollLeft <= 2;
      next.hidden = this.track.scrollLeft >= overflow - 2;
    }

    /* CARD IMAGE SCROLLER - ask 2, screenshots 9 and 10.
       Desktop: a quiet chevron inside each edge of the photo on hover, one photo a click.
       Phone: swipe the photo, with a short position bar under it - one segment a photo, the one you
       are on in Iron and the rest faint. That bar is not new furniture; it takes over the static
       90x3 Iron rule the card already draws (.product-item__info::before), the thing he circled in
       screenshot 10. Same box, so no layout shift; a card with one photo keeps the plain rule.
       It shows the CURRENT colour's photos and resets on a colour change - useColor() is the only
       thing that moves this.color and it zeroes the index.
       It does not fight the panel: a touch starting inside the panel never moves the photo, and
       while the panel is open a swipe does nothing. The ARROWS stay live while the panel is up,
       which is what ASRV does in screenshots 11 and 12 - here the panel opens on hover, so hiding
       them whenever it was open would mean never showing them. Vertically centred at z-index 3,
       under the panel (4) and the "+" (5), so they cannot cover the disc.
       No new library and no carousel: the photo is swapped on the card's own <img>, decoded
       off-screen first so the swap cannot flash. */

    /* The one place this.color moves. Anything that changes the colour goes through here, which is
       what makes "changing colour resets the scroller" true by construction rather than by
       remembering to reset it in three places. */
    useColor(value) {
      /* The first colour this card is ever put on is the one it was rendered for: render() and
         prebuild() both resolve it through currentColor() before anything else can move it. */
      if (this._ownColor === undefined) this._ownColor = value;
      if (this.color === value && this._photos) return;
      this.color = value;
      this._photo = 0;
      this._photos = this.photosOf(value);
      this._failed = false;
    }

    /* REVISION 4, regression S7. photosFor() leads with the colour's own featured image, but a card
       can be rendered with a different photo of that colour - the product's featured image. Measured
       on amore-sports-bra (Archive, Black): the card showed 779548, photo one was the variant's
       750593 and 779548 was photo two, so the bar read 1/4 and the first "next" swapped to the
       picture already on screen. On the card's own colour, the photo the card is showing goes first.
       "Own colour" is the colour the card was rendered for (_ownColor), not data-color: a product
       with a single colour - amore-sports-bra is one - is never split into colour cards, so it
       carries no data-color at all.
       A copy is reordered, never the model, which every colour card of the product shares. */
    photosOf(value) {
      if (!this.model) return [];
      var colors = this.model.colors;
      var list = null;
      var i;
      for (i = 0; i < colors.length; i++) {
        if (colors[i].value === value) { list = colors[i].photos || []; break; }
      }
      if (!list) return (colors[0] && colors[0].photos) || [];
      if (this._cardFile && value === this._ownColor) {
        for (i = 1; i < list.length; i++) {
          if (fileOf(list[i]) === this._cardFile) {
            list = list.slice();
            list.unshift(list.splice(i, 1)[0]);
            break;
          }
        }
      }
      return list;
    }

    /* Is this card inside a sideways product row? Asked once, and only by a card that has photos to
       move. See inHorizontalScroller(). */
    inRow() {
      if (this._inRow == null) this._inRow = inHorizontalScroller(this.card || this);
      return this._inRow;
    }

    /* The bar and the arrows are created from here rather than printed by snippets/product-item.
       liquid, so no theme file outside this feature's own had to change to carry them.
       REVISION 4: split in two. The BAR is mounted at rest, from the photo count the Liquid prints
       (data-photos), so a phone shopper sees the swipe affordance Pedram circled in screenshot 10
       before anything has been fetched (regression S6). The ARROWS wait for the photo list, because
       an arrow is a promise that something will move. */
    barMount() {
      if (this.bar || !this.card) return;
      var info = this.card.querySelector('.product-item__info');
      if (!info) return;
      var bar = document.createElement('div');
      bar.className = 'dw-qa__bar';
      bar.setAttribute('aria-hidden', 'true');
      info.insertBefore(bar, info.firstChild);
      this.bar = bar;
      this.info = info;
    }

    navMount() {
      if (this.nav || !this.card) return;
      /* Before anything can repaint the photo, record what the server sent - restoreCard() is what
         puts the card back, and it can only do that from a snapshot taken first. */
      this.snapshot();
      var wrapper = this.card.querySelector('.product-item__image-wrapper');
      if (!wrapper) return;
      var nav = document.createElement('div');
      nav.className = 'dw-qa__nav';
      /* aria-hidden and tabindex -1 together, deliberately. These are a pointer-only convenience:
         the CSS only paints them on a hover-capable fine pointer, and every photo behind them is
         on the product page the card already links to. Left focusable they would have added two
         tab stops to every card - 218 on a 109-card collection page - for a control a keyboard
         shopper cannot see. The swatch chevrons inside the panel stay focusable, because the panel
         is a real chooser and its colours are not reachable any other way from the grid. */
      nav.setAttribute('aria-hidden', 'true');
      nav.innerHTML =
        '<button type="button" tabindex="-1" class="dw-qa__page dw-qa__page--prev" hidden>' +
        chevron('prev') + '</button>' +
        '<button type="button" tabindex="-1" class="dw-qa__page dw-qa__page--next" hidden>' +
        chevron('next') + '</button>';
      wrapper.appendChild(nav);
      nav.addEventListener('click', this.onNavClick.bind(this));
      /* SPEED S3 (critique round 1): the first arrow click used to wait for its own photo - 447ms
         measured on a rendition the CDN edge had not cached yet, with nothing on screen to say the
         click had landed. The photo an arrow will show is now fetched as soon as the pointer
         settles on that arrow (80ms, because a pointer sweeping across a row passes through every
         card's arrows and should not pay for them), and at the latest on the press itself, which
         is ~100ms before the click. It costs one photo, only for a shopper aiming at an arrow. */
      var self = this;
      var pageOf = function (event) {
        return event.target && event.target.closest ? event.target.closest('.dw-qa__page') : null;
      };
      var intent = function (button) {
        self.preloadAt(self._photo + (button.classList.contains('dw-qa__page--prev') ? -1 : 1), true);
      };
      nav.addEventListener('pointerover', function (event) {
        var button = pageOf(event);
        if (!button) return;
        clearTimeout(self._navDwell);
        self._navDwell = setTimeout(function () { intent(button); }, 80);
      });
      nav.addEventListener('pointerout', function () { clearTimeout(self._navDwell); });
      nav.addEventListener('pointerdown', function (event) {
        var button = pageOf(event);
        if (button) intent(button);
      });
      this.nav = nav;
    }

    /* How many photos this card can move through: the real list once it is in hand, the Liquid's
       count before that, and -1 when neither knows. */
    photoCount() {
      if (this._photos) return this._photos.length;
      return this._hint == null ? -1 : this._hint;
    }

    /* Paint the bar's segments and decide which arrows are reachable. Called after every render and
       every step, and cheap enough to be: the segment count only changes when the colour does. */
    scrollerSync() {
      var n = this._photos ? this._photos.length : Math.max(this._hint || 0, 0);
      if (n > 1) {
        this.barMount();
        if (this._photos) this.navMount();
      }
      if (this.bar) {
        if (n > 1) {
          if (this.bar.children.length !== n) {
            var html = '';
            for (var i = 0; i < n; i++) html += '<span></span>';
            this.bar.innerHTML = html;
          }
          for (var j = 0; j < this.bar.children.length; j++) {
            this.bar.children[j].className = j === this._photo ? 'is-on' : '';
          }
          this.bar.hidden = false;
          if (this.info) this.info.classList.add('dw-qa-bar-on');
        } else {
          this.bar.hidden = true;
          /* Hand the plain Iron rule back: a colour with one photo has no position to show. */
          if (this.info) this.info.classList.remove('dw-qa-bar-on');
        }
      }
      /* Only a card that really has somewhere to swipe to takes touch-action: pan-y on its photo, and
         never one inside a sideways row (regression B1). A colour with one photo behaves exactly as
         it does today. */
      if (this.card) this.card.classList.toggle('dw-qa-scroll', n > 1 && !this.inRow());
      if (this.nav) {
        /* Both arrows whenever there is anywhere to go (scroller S3): Pedram circled a PAIR in
           screenshot 12, and revision 3 hid the left one on photo one, which is where every card
           starts. They wrap at the ends instead - left from photo one goes to the last photo. */
        var prev = this.nav.querySelector('.dw-qa__page--prev');
        var next = this.nav.querySelector('.dw-qa__page--next');
        if (prev) prev.hidden = n < 2;
        if (next) next.hidden = n < 2;
      }
    }

    /* The arrows wrap (wrap = true); a swipe is clamped, with resistance at the ends instead. */
    step(delta, wrap) {
      var photos = this._photos || [];
      var n = photos.length;
      if (n < 2) return;
      var next = this._photo + delta;
      if (wrap) next = ((next % n) + n) % n;
      else next = Math.max(0, Math.min(n - 1, next));
      if (next === this._photo) return;
      this._photo = next;
      this.showPhoto();
      this.scrollerSync();
      this.preloadAt(next + (delta < 0 ? -1 : 1), wrap);
      if (this.live) this.live.textContent = (next + 1) + ' / ' + n;
    }

    /* The width the photo is actually drawn at, rounded UP to a hundred so every card on the page
       asks the CDN for the same rendition and the second card's photos are cache hits. Up, not to
       the nearest (scroller S5): at DPR 1 a 328px box was asking for width=300 and drawing it 9%
       larger than it came, so photos two onwards looked softer than photo one. */
    photoWidth() {
      var wrapper = this.card && this.card.querySelector('.product-item__image-wrapper');
      var width = wrapper ? wrapper.clientWidth : 400;
      var dpr = window.devicePixelRatio || 1;
      if (dpr > 2) dpr = 2;
      var want = Math.ceil(width * dpr / 100) * 100;
      if (want < 200) want = 200;
      if (want > 1400) want = 1400;
      return want;
    }

    /* Decode first, then swap. Setting src straight onto a visible <img> shows the box empty until
       the new photo lands, which on a card is a white flash over the product. */
    showPhoto() {
      var photos = this._photos || [];
      var src = photos[this._photo];
      if (!src) return;
      var url = sized(src, this.photoWidth());
      var self = this;
      var apply = function () {
        if ((self._photos || [])[self._photo] !== src) return;   /* moved on while it loaded */
        self.applyPhoto(url);
      };
      var probe = new Image();
      probe.onload = apply;
      probe.onerror = apply;
      probe.src = url;
      if (probe.complete) apply();
    }

    applyPhoto(url) {
      var image = this.primaryImage();
      if (!image) return;
      /* Whatever path gets here, the server's photo is recorded before it is replaced. */
      this.snapshot();
      /* One url, not a srcset: the rendition asked for is already the one the box draws, and a
         srcset would let the browser pick a candidate the preload above did not decode - which is
         the flash this method exists to avoid. restoreCard() puts Focal's own srcset back. */
      image.removeAttribute('srcset');
      image.removeAttribute('sizes');
      image.src = url;
      image.removeAttribute('data-media-id');
      /* Focal swaps a card to its SECOND photo on hover. Off the first photo, or on a colour the
         card was not rendered for, that swap would show a photo the shopper has just moved away
         from, so it is suppressed exactly while that is true and handed back by restoreCard(). */
      var wrapper = this.card.querySelector('.product-item__image-wrapper');
      if (wrapper) wrapper.classList.remove('product-item__image-wrapper--multiple');
    }

    primaryImage() {
      return this.card && this.card.querySelector('.product-item__primary-image:not([hidden])');
    }

    /* One photo, fetched ahead and decoded off-screen. Only ever for a card the shopper is actually
       moving through: after a step, on a swipe that has been recognised as one, on a colour change,
       or on a pointer settling on an arrow (navMount). Never on a plain hover - with the open delay
       at zero a mouse crossing a row opens every card it touches, and a photo per card crossed is
       most of a megabyte over a sweep. */
    preloadAt(index, wrap) {
      var photos = this._photos || [];
      var n = photos.length;
      if (n < 2) return;
      if (wrap) index = ((index % n) + n) % n;
      var src = photos[index];
      if (!src) return;
      var url = sized(src, this.photoWidth());
      if (!this._pre) this._pre = Object.create(null);
      if (this._pre[url]) return;
      this._pre[url] = true;
      var probe = new Image();
      probe.decoding = 'async';
      probe.src = url;
    }

    preloadNext() {
      this.preloadAt(this._photo + 1, false);
    }

    onNavClick(event) {
      var button = event.target.closest ? event.target.closest('.dw-qa__page') : null;
      if (!button) return;
      /* The chevron sits on top of Focal's link to the product page. */
      event.preventDefault();
      event.stopPropagation();
      this.step(button.classList.contains('dw-qa__page--prev') ? -1 : 1, true);
    }

    /* ---- the phone swipe --------------------------------------------------------------------
       REVISION 4 (critique round 1: speed B1, scroller S2, regression B1 / S5 / S8).
       - touchstart only RECORDS where the finger landed. It fetches nothing and arms nothing, so a
         shopper scrolling the grid - whose finger lands on a card on nearly every flick - costs
         nothing (speed B1).
       - The first touchmove past 10px decides. More up-and-down than sideways: a scroll, and this
         card lets go of it completely. More sideways: a SWIPE, and that is the phone's "hover or
         tap" - warming arms, this card's own product is fetched at once (the JSON is in flight
         before the finger lifts), and the photo starts to follow the finger.
       - It FOLLOWS THE FINGER now (scroller S2, regression S8, speed S5), which is the peek Pedram
         circled in screenshot 9: the photo moves under the thumb with the next one sliding in
         beside it (.dw-qa__ghost), with resistance at the first and last photo. Released past 36px
         it settles onto the next photo in 160ms; short of that it springs back. touch-action:
         pan-y on the photo is what hands the sideways motion to this code, so nothing is ever
         preventDefault-ed and vertical scrolling is untouched.
       - Never inside a sideways row (inRow, regression B1), never while the panel is open, never
         from a touch that starts on the panel, and never from the touch that dismissed a panel. */

    onCardDown(event) {
      this.swipeStart(event);
    }

    swipeStart(event) {
      this._sw = null;
      var touches = event.touches;
      /* One finger only: a pinch or a two-finger scroll is not a swipe. */
      if (!touches || touches.length !== 1) return;
      /* While the panel is open the photo stands still, and a touch that starts inside the panel
         never moves it. onOutside() runs on the document in the CAPTURE phase, so by the time this
         listener sees a touch that dismissed an open panel, isOpen is already false - _dismissAt is
         what remembers that this touch was a dismissal and not a swipe. */
      if (this.isOpen) return;
      if (now() - (this._dismissAt || 0) < 500) return;
      if (this.panel && this.panel.contains(event.target)) return;
      if (event.target.closest && event.target.closest('.dw-qa__plus, .dw-qa__nav')) return;
      /* A card whose colour is known to have one photo has nowhere to go. */
      var count = this.photoCount();
      if (count >= 0 && count < 2) return;
      if (this.inRow()) return;
      this._sw = { x: touches[0].clientX, y: touches[0].clientY, t: now(), axis: null, dx: 0, dy: 0 };
    }

    onCardMove(event) {
      var sw = this._sw;
      if (!sw) return;
      var touches = event.touches;
      if (!touches || touches.length !== 1) {
        this._sw = null;
        this.dragSettle(false);
        return;
      }
      var dx = touches[0].clientX - sw.x;
      var dy = touches[0].clientY - sw.y;
      sw.dx = dx;
      sw.dy = dy;
      if (!sw.axis) {
        if (Math.abs(dx) < 10 && Math.abs(dy) < 10) return;
        if (Math.abs(dx) <= Math.abs(dy)) {
          /* A scroll. Let go of it entirely: nothing fetched, nothing moved. */
          this._sw = null;
          return;
        }
        sw.axis = 'x';
        this.swipeRecognised();
      }
      this.dragTo(dx);
    }

    /* The gesture is a swipe on this card's photo: the phone equivalent of pointing at it. */
    swipeRecognised() {
      this._swipedAt = now();
      warmArm();
      if (!this.model) this.warm();
      this.dragBegin();
    }

    onCardUp(event) {
      var sw = this._sw;
      this._sw = null;
      if (!sw || this.isOpen) {
        this.dragSettle(false);
        return;
      }
      var touch = event.changedTouches && event.changedTouches[0];
      var dx = touch ? touch.clientX - sw.x : sw.dx;
      var dy = touch ? touch.clientY - sw.y : sw.dy;
      if (!sw.axis) {
        /* No touchmove reached this card (a browser can own a gesture outright). Judge it from its
           two ends, as revision 3 did: sideways, decisive and quick, or it was not a swipe. */
        if (Math.abs(dx) < 36 || Math.abs(dx) < Math.abs(dy) * 1.4 || now() - sw.t > 900) return;
        this.swipeRecognised();
      }
      this._swipedAt = now();
      var dir = dx < 0 ? 1 : -1;
      if (Math.abs(dx) < 36) {
        this.dragSettle(false);
        return;
      }
      if ((this._photos || []).length < 2) {
        /* The photo list is still in flight. Spring back and hold the gesture rather than dropping
           it; flushPending() plays it the moment the list lands, and it expires if that takes
           longer than a shopper would still connect the two. */
        this.dragSettle(false);
        this._pending = { dir: dir, t: now() };
        return;
      }
      this.dragSettle(true, dir);
    }

    flushPending() {
      var pending = this._pending;
      if (!pending) return;
      if ((this._photos || []).length < 2) return;
      this._pending = null;
      if (now() - pending.t > 1200) return;
      this.step(pending.dir, false);
    }

    /* The browser took the gesture over. Not a swipe, and not a tap. */
    onCardCancel() {
      this._sw = null;
      this.dragSettle(false);
    }

    dragBegin() {
      /* A new swipe while the last one is still settling: land that one first. */
      if (this._settle) this.dragFinish();
      var image = this.primaryImage();
      if (!image || !image.parentNode) return;
      var width = image.parentNode.clientWidth || image.clientWidth || 0;
      if (!width) return;
      image.style.transition = 'none';
      image.style.willChange = 'transform';
      this._drag = { image: image, width: width, pw: this.photoWidth(), x: 0, target: -1 };
    }

    /* The neighbour that slides in beside the photo. One per card, made on the first swipe, and
       styled from the card's own <img> so it crops exactly the way the photo does. */
    ghost(image) {
      var ghost = this._ghost;
      if (!ghost) {
        ghost = document.createElement('img');
        ghost.className = 'dw-qa__ghost';
        ghost.alt = '';
        ghost.setAttribute('aria-hidden', 'true');
        ghost.decoding = 'async';
        try {
          var cs = window.getComputedStyle(image);
          ghost.style.objectFit = cs.objectFit;
          ghost.style.objectPosition = cs.objectPosition;
        } catch (e) {}
        this._ghost = ghost;
      }
      if (ghost.parentNode !== image.parentNode) image.parentNode.insertBefore(ghost, image.nextSibling);
      return ghost;
    }

    dragTo(dx) {
      var d = this._drag;
      if (!d) return;
      var photos = this._photos;
      var n = photos ? photos.length : this._hint;
      var dir = dx < 0 ? 1 : -1;
      var target = this._photo + dir;
      /* Is there a photo on that side? From the list once it is here, from the Liquid's count
         until then. No: the photo still moves, at a third of the finger's travel, and comes back. */
      var exists = n > 1 && target >= 0 && target <= n - 1;
      var x = exists ? dx : dx * 0.33;
      d.x = x;
      d.image.style.transform = 'translate3d(' + x + 'px,0,0)';
      if (exists) {
        var ghost = this.ghost(d.image);
        /* The list can land mid-drag, so the source is re-checked on every move. Until it does, the
           neighbour is the card's own grey ground, which is what an unloaded photo looks like. */
        var src = photos && photos[target] ? sized(photos[target], d.pw) : '';
        if (d.target !== target || (src && ghost.getAttribute('src') !== src)) {
          d.target = target;
          if (src) ghost.setAttribute('src', src);
          else ghost.removeAttribute('src');
        }
        ghost.style.transition = 'none';
        ghost.style.visibility = 'visible';
        ghost.style.transform = 'translate3d(' + (x + (dir > 0 ? d.width : -d.width)) + 'px,0,0)';
      } else if (this._ghost) {
        this._ghost.style.visibility = 'hidden';
      }
    }

    /* Release: settle onto the next photo, or spring back. 160ms, the one motion the spec allows. */
    dragSettle(commit, dir) {
      var d = this._drag;
      if (!d) return;
      this._drag = null;
      var self = this;
      var image = d.image;
      var ghost = this._ghost;
      var photos = this._photos || [];
      var target = this._photo + (dir || 0);
      var ok = !!commit && photos.length > 1 && target >= 0 && target < photos.length && !!ghost;
      var ease = 'transform 160ms ease-out';
      image.style.transition = ease;
      if (ghost) ghost.style.transition = ease;
      if (ok) {
        var url = sized(photos[target], d.pw);
        if (ghost.getAttribute('src') !== url) ghost.setAttribute('src', url);
        ghost.style.visibility = 'visible';
        image.style.transform = 'translate3d(' + (dir > 0 ? -d.width : d.width) + 'px,0,0)';
        ghost.style.transform = 'translate3d(0,0,0)';
        this.snapshot();
        this._photo = target;
        /* The bar moves with the finger's decision, not 160ms later. */
        this.scrollerSync();
        if (this.live) this.live.textContent = (target + 1) + ' / ' + photos.length;
        this._settle = { image: image, url: url, commit: true };
      } else {
        image.style.transform = 'translate3d(0,0,0)';
        if (ghost) ghost.style.transform = 'translate3d(' + (d.x < 0 ? d.width : -d.width) + 'px,0,0)';
        this._settle = { image: image, commit: false };
      }
      clearTimeout(this._settleTimer);
      this._settleTimer = setTimeout(function () { self.dragFinish(); }, 180);
    }

    /* The new photo is already decoded in the ghost. It is put on the card's own <img> while that
       <img> is still off to the side, and the ghost is only dropped once the <img> has decoded it -
       so there is never a frame with an empty box, and the photo on the card is the card's own
       element again (restoreCard, the panel and Focal all keep working on it). */
    dragFinish() {
      var settle = this._settle;
      clearTimeout(this._settleTimer);
      this._settleTimer = null;
      this._settle = null;
      if (!settle) return;
      var self = this;
      var image = settle.image;
      var reset = function () {
        /* A newer swipe owns the photo now, dragging or settling; leave it alone. */
        if (self._drag || self._settle) return;
        /* Focal's own transition on this <img> is opacity and visibility only, so clearing the
           inline transform and transition together is an instant jump, not an animated one. */
        image.style.transition = '';
        image.style.transform = '';
        image.style.willChange = '';
        if (self._ghost) {
          self._ghost.style.transition = '';
          self._ghost.style.transform = '';
          self._ghost.style.visibility = 'hidden';
        }
      };
      if (!settle.commit || (this._photos || [])[this._photo] == null) {
        reset();
        return;
      }
      this.applyPhoto(settle.url);
      var done = function () {
        reset();
        self.preloadNext();
      };
      if (image.decode) image.decode().then(done, done);
      else done();
    }

    onCardClick(event) {
      if (!this._swipedAt || now() - this._swipedAt > 400) return;
      /* A swipe that ends on the photo would otherwise be delivered as a click on Focal's link and
         navigate to the product page. */
      event.preventDefault();
      event.stopPropagation();
    }

    onPanelClick(event) {
      /* The shopper is using the panel now, so the card is allowed to agree with it (see open()). */
      this._openedByHover = false;
      var arrow = event.target.closest('.dw-qa__arrow');
      if (arrow && this.track) {
        event.preventDefault();
        var step = Math.max(80, Math.round(this.track.clientWidth * 0.8));
        var left = arrow.classList.contains('dw-qa__arrow--prev') ? -step : step;
        /* CRITIQUE QA3-S2. Instant, not smooth. The 98px glide was still running ~700ms after the
           chevron was released, so the colour the shopper aimed at had moved out from under the
           finger: a tap 200ms later landed three swatches along (measured on
           power-play-oversized-shirt, 12 colours - aimed Light Grey, got Red). Same family as
           Pedram's bug, one control over. The strip is now where it looks before the next tap can
           land. 'auto' is named rather than left to default so a page-level scroll-behavior:smooth
           can never put the glide back. */
        this.track.scrollBy({ left: left, behavior: 'auto' });
        return;
      }
      var swatch = event.target.closest('.dw-qa__sw');
      if (swatch) {
        event.preventDefault();
        this.selectColor(swatch.getAttribute('data-color'));
        return;
      }
      var size = event.target.closest('.dw-qa__size');
      if (size && !size.disabled && !this._busy) {
        event.preventDefault();
        if (!this.sizeTapIsOwn(event, size)) return;
        this.add(size);
      }
    }

    selectColor(value) {
      if (value === this.color) return;
      /* Not "this.color = value": useColor() is what resets the scroller to the new colour's first
         photo, which is ask 2's "changing colour resets it". */
      this.useColor(value);
      this.render();
      this.paintCard();
      this.preloadNext();

      var current = this.currentColor();
      /* The focus rescue that used to sit here is gone with the thing it rescued. render() no
         longer throws the swatch away - update() only moves the mark - so the button the shopper
         activated still exists and still has focus, and calling focus() on it again would have
         scrolled a phone to it for no reason. */
      if (current && this.live) {
        this.live.textContent = current.value + (current.available ? '' : ', ' + str('soldOut'));
      }
    }

    /* Everything paintCard() can change, recorded once while the card is still exactly what the
       server rendered. Restoring from this is how a card returns to its own colour with its own
       price markup, its own labels and its own hover-image behaviour intact (critique QA-8). */
    snapshot() {
      if (this._snap || !this.card) return;
      var image = this.card.querySelector('.product-item__primary-image:not([hidden])');
      var wrapper = this.card.querySelector('.product-item__image-wrapper');
      var name = this.card.querySelector('.product-item-meta__color-name');
      var priceList = this.card.querySelector('.product-item-meta .price-list');
      var labelList = this.card.querySelector('.product-item__label-list');
      learn(priceList);
      var links = this.card.querySelectorAll('a[href*="/products/"]');
      var hrefs = [];
      for (var i = 0; i < links.length; i++) hrefs.push(links[i].getAttribute('href'));
      this._snap = {
        /* PORT FIX 2026-09-20, moving this feature onto "CRO Sandbox - Sep.14 - Product cards".
           This records the colour the CARD was rendered for, read from the attribute the Liquid
           printed, and no longer this.color - which render() has already set to whatever the panel
           resolved, two lines above the call to this method. Recording the resolved colour made
           _snap.color === current.value on EVERY first render, so paintCard()'s "already on this
           card's own colour" short-circuit always fired and the repaint the block below it
           describes (critique R4-S1) never actually ran. Measured on this theme, /search?q=wrist
           wrap: the 4-Stripe Wrist Wrap card shows a group photo of all four colours, which is the
           product's featured image and not any variant's, so currentColor()'s photo match finds
           nothing and falls through to the first colour - and the card went on showing the group
           shot while the panel had silently selected Olympic Blue & Black. A shopper who tapped
           Add without reading the strip bought a colour they were never shown.
           null is the honest value for a card that was rendered for no single colour: it lets
           paintCard() move the card onto whatever the panel has selected, and close() still puts
           the card back exactly as the server sent it. A colour-split card carries data-color, so
           for those this is the same value as before and nothing changes. */
        color: this.getAttribute('data-color') || this._photoColor || null,
        src: image ? image.getAttribute('src') : null,
        srcset: image ? image.getAttribute('srcset') : null,
        /* The scroller drops sizes with srcset when it paints one rendition, so it is recorded here
           too or a restored card would keep Focal's srcset and lose the sizes that select from it. */
        sizes: image ? image.getAttribute('sizes') : null,
        mediaId: image ? image.getAttribute('data-media-id') : null,
        multiple: wrapper ? wrapper.classList.contains('product-item__image-wrapper--multiple') : false,
        name: name ? name.textContent : null,
        priceHTML: priceList ? priceList.innerHTML : null,
        labelHTML: labelList ? labelList.innerHTML : null,
        /* A card with no labels at all has no list element for Focal's own "Sold out" to live in,
           so paintSoldOutLabel() creates one where Focal puts it. This records that it was not
           there, so restoreCard() takes it away again rather than leaving an empty div behind. */
        hadLabelList: !!labelList,
        hrefs: hrefs
      };
    }

    restoreCard() {
      var snap = this._snap;
      if (!snap) return;
      var image = this.card.querySelector('.product-item__primary-image:not([hidden])');
      if (image) {
        if (snap.src == null) image.removeAttribute('src'); else image.setAttribute('src', snap.src);
        if (snap.srcset == null) image.removeAttribute('srcset'); else image.setAttribute('srcset', snap.srcset);
        if (snap.sizes == null) image.removeAttribute('sizes'); else image.setAttribute('sizes', snap.sizes);
        if (snap.mediaId == null) image.removeAttribute('data-media-id');
        else image.setAttribute('data-media-id', snap.mediaId);
      }
      var wrapper = this.card.querySelector('.product-item__image-wrapper');
      if (wrapper && snap.multiple) wrapper.classList.add('product-item__image-wrapper--multiple');
      var name = this.card.querySelector('.product-item-meta__color-name');
      if (name && snap.name != null) name.textContent = snap.name;
      var priceList = this.card.querySelector('.product-item-meta .price-list');
      if (priceList && snap.priceHTML != null) priceList.innerHTML = snap.priceHTML;
      var labelList = this.card.querySelector('.product-item__label-list');
      if (labelList) {
        if (snap.hadLabelList) {
          if (snap.labelHTML != null) labelList.innerHTML = snap.labelHTML;
        } else if (labelList.parentNode) {
          labelList.parentNode.removeChild(labelList);
        }
      }
      var links = this.card.querySelectorAll('a[href*="/products/"]');
      for (var i = 0; i < links.length && i < snap.hrefs.length; i++) {
        if (snap.hrefs[i] != null) links[i].setAttribute('href', snap.hrefs[i]);
      }
    }

    /* Move the card itself over to the chosen colour - image, colour name, price, labels and links -
       the way the grid would have rendered that colour's own card. */
    paintCard() {
      if (!this.card || !this.model) return;
      var current = this.currentColor();
      if (!current) return;

      if (this._snap && current.value === this._snap.color) {
        this.restoreCard();
        return;
      }

      /* The photo is the scroller's to paint - one place writes this <img>, so the position bar and
         the picture can never disagree. showPhoto() also drops Focal's hover-to-second-image class:
         the second <img> still shows the colour the card was rendered for, so swapping to it would
         show a colour the shopper did not pick. restoreCard() puts the class back. */
      this.showPhoto();

      var name = this.card.querySelector('.product-item-meta__color-name');
      if (name && current.value) name.textContent = current.value;

      /* Never link to a variant that cannot be bought: a fully sold-out colour links to the plain
         product page rather than preselecting a dead variant (critique QA-3). */
      var sellable = current.sizes.filter(function (size) { return size.available; })[0];
      var href = this.model.url.split('?')[0] + (sellable ? '?variant=' + sellable.id : '');
      var links = this.card.querySelectorAll('a[href*="/products/"]');
      for (var i = 0; i < links.length; i++) links[i].setAttribute('href', href);

      this.paintSoldOutLabel(current);
      this.paintPrice(current);
    }

    /* CRITIQUE S1 (edge-data). Switching to a colour that is entirely sold out repainted the card
       to that colour's photo, name and price with no "Sold out" chip, so the card read as
       buyable while every size chip under it was struck through and inert. paintDiscountLabel()
       already knew - it drops the "% OFF" chip when the colour is unavailable - so the silence was
       an oversight, not a decision. Focal renders this label unconditionally from
       {{ product.available }}, so putting it back is reproducing what the server would have sent
       for this state, not inventing card furniture from a guess about the theme's settings. */
    paintSoldOutLabel(color) {
      var wrapper = this.card.querySelector('.product-item__image-wrapper');
      if (!wrapper) return;
      var list = wrapper.querySelector('.product-item__label-list');
      var chip = list && list.querySelector('.label--subdued');
      if (color.available) {
        if (chip && chip.parentNode) chip.parentNode.removeChild(chip);
        return;
      }
      if (chip) return;
      if (!list) {
        list = document.createElement('div');
        list.className = 'product-item__label-list label-list';
        /* Focal renders this as the first child of the image wrapper. */
        wrapper.insertBefore(list, wrapper.firstChild);
      }
      list.insertAdjacentHTML('beforeend', soldOutLabelHTML());
    }

    /* Rebuild the WHOLE price line for the chosen colour - sale price, struck-through compare-at
       price and the discount chip together. Repainting only the selling price is what made a card
       claim a discount that did not exist, and hide one that did (critique blocker QA-1 / B1). */
    paintPrice(color) {
      var facts = priceFacts(color);
      if (!facts) return;
      var priceText = money(facts.min);
      if (!priceText) return;

      var priceList = this.card.querySelector('.product-item-meta .price-list');
      if (priceList) {
        if (facts.varies) {
          priceText = String(learned('from', 'fromPrice')).split(PRICE_TOKEN).join(priceText);
        }
        var html = '<span class="price' + (facts.onSale ? ' price--highlight' : '') + '">' +
          '<span class="visually-hidden">' + esc(learned('sale', 'salePrice')) + '</span>' + priceText + '</span>';
        if (facts.onSale) {
          html += '<span class="price price--compare"><span class="visually-hidden">' +
            esc(learned('regular', 'regularPrice')) + '</span>' + money(facts.compare) + '</span>';
        }
        /* Keep any unit-price block or other sibling Focal rendered after the price spans. */
        var extra = priceList.querySelector('.price--block');
        if (extra) html += extra.outerHTML;
        priceList.innerHTML = html;
      }

      this.paintDiscountLabel(color, facts);
    }

    /* The "% OFF" chip. settings.show_discount is off on this theme today, so no card carries one
       and this is a no-op - but if it is switched on before Monday, a chip left over from the
       previous colour would be the same false claim as a stale compare-at price.
       Deliberately asymmetric, and deliberately driven by the card rather than by a setting: a chip
       that no longer applies is always removed, but one is only ever written into a card that
       already had one. Leaving a chip off is an omission; inventing card furniture from a guess
       about how the theme is configured is a risk. */
    paintDiscountLabel(color, facts) {
      var list = this.card.querySelector('.product-item__label-list');
      if (!list) return;
      var chip = list.querySelector('.label--highlight');
      if (!chip) return;
      if (!facts.onSale || !color.available) {
        chip.parentNode.removeChild(chip);
        return;
      }
      var mode = settings().discountMode;
      if (mode === 'percentage') {
        chip.textContent = Math.round((facts.compare - facts.min) * 100 / facts.compare) + '% OFF';
      } else if (mode === 'saving') {
        chip.innerHTML = '—' + money(facts.compare - facts.min);
      }
      /* Any other mode is a fixed word ("Sale") that does not change with the colour - leave it. */
    }

    /* A card that could never need the status row did not render one (needsStatus()), and a refused
       add still has to be able to speak. Creating it here changes the panel's height once, at the
       one moment the shopper is being asked to stop and read - and sizeTapIsOwn() already refuses
       any add whose press began before the last render, so the move cannot turn into a second add. */
    ensureStatus() {
      if (this.statusLine || !this.panel || !this.sizeRow) return;
      var line = document.createElement('p');
      line.className = 'dw-qa__status';
      this.panel.insertBefore(line, this.sizeRow);
      this.statusLine = line;
    }

    add(button) {
      var input = this.form.querySelector('[name="id"]');
      if (!input || this._busy) return;
      this._busy = true;
      input.value = button.getAttribute('data-id');

      /* Match the product page exactly. snippets/final-sale-notice.liquid makes a variant final
         sale two ways: the whole product is in The Archive, OR that one variant carries
         custom.final_sale. The Archive case is a plain enabled input in the Liquid and is not
         touched here. This is the second case - a clearance colour on an otherwise returnable
         product - where the flag is true for some variants and false for others, so it can only be
         decided once the shopper has picked one. A disabled input is not submitted, so the default
         is "no claim": if this code never runs, a returnable item is never labelled non-refundable.
         Six live listings need this today, all sports bras and leggings with some clearance
         colours. Without it a shopper could buy a non-refundable colour from a card having seen no
         disclosure, with nothing on the order line to show it. */
      var finalInput = this.form.querySelector('[data-final-optional]');
      if (finalInput) {
        finalInput.disabled = this._finalIds.indexOf(String(input.value)) === -1;
      }
      button.setAttribute('aria-busy', 'true');
      this._failed = false;
      this.syncStatus();

      var self = this;
      var settled = false;
      var timer = null;
      /* One teardown for all three exits - the answer, the timeout, and the card being removed from
         the page while the add is in flight. */
      var done = function () {
        settled = true;
        clearTimeout(timer);
        self.form.removeEventListener('cart-notification:show', onResult);
        self._addDone = null;
        button.removeAttribute('aria-busy');
        self._busy = false;
        /* A successful add closes the panel while the pointer is very likely still resting on the
           card. Without this, hover would raise it again behind the cart drawer a frame later. */
        self._hoverHold = now() + 1200;
      };
      var onResult = function (event) {
        if (settled) return;
        done();
        if (!event.detail || event.detail.status === 'success') {
          self.close(false);
        } else {
          /* The refusal usually means this variant went out of stock since the panel was filled, so
             the cached list is stale; drop it and the next open shows an honest faded chip
             (critique QA4-S1). */
          dropVariants(self.handle);
          self._failed = true;
          self.ensureStatus();
          self.syncStatus();
          self.measure();
        }
      };
      /* Silence is not failure. This guard only means the theme has not answered yet: on sale
         morning a slow /cart/add.js can outrun it while the item is genuinely going into the bag,
         and saying "could not be added" then is a lie that invites a second tap and a duplicate
         line. Release the button, leave the panel open, claim nothing - the real failure path still
         speaks, because Focal always fires cart-notification:show with status 'error' when the add
         is refused (critique QA2-S3). */
      var onTimeout = function () {
        if (settled) return;
        done();
      };
      timer = setTimeout(onTimeout, 10000);
      this._addDone = done;
      this.form.addEventListener('cart-notification:show', onResult);

      if (typeof this.form.requestSubmit === 'function') this.form.requestSubmit();
      else this.form.dispatchEvent(new Event('submit', { bubbles: true, cancelable: true }));
    }
  }

  if (!window.customElements.get('dw-quick-add')) {
    window.customElements.define('dw-quick-add', DwQuickAdd);
  }

  /* CRITIQUE S1 (publish-risk). This class is what lets assets/dw-quick-add.css hide Focal's own
     quick view and show the "+". Set here, at the end of the only code path that defines the
     element, so a browser that fails the guard at the top of this file, or a page where this script
     is blocked or never arrives, keeps Focal's quick view and shows no dead disc. */
  document.documentElement.classList.add('dw-qa-ready');
})();